/**
 * Clés `videos.page.studioButton` et `videos.studio.*` pour les langues partielles
 * (fusionnées dans i18n après les bundles principaux, avant withEnglishFallback merge order:
 * en base + gap + ce fichier → les clés ici remplacent l’anglais).
 */
export type VideosStudioLocaleCode = 'de' | 'es' | 'it' | 'pt' | 'lb' | 'uk' | 'ru' | 'ar' | 'tr';

export const videosStudioLocales: Record<VideosStudioLocaleCode, Record<string, string>> = {
  de: {
    'videos.page.studioButton': 'Studio',
    'videos.studio.title': 'Videostudio',
    'videos.studio.rail.prep': 'Vorb.',
    'videos.studio.rail.capture': 'Aufnahme',
    'videos.studio.rail.post': 'Schnitt',
    'videos.studio.rail.aria': 'Schritte im Videostudio',
    'videos.studio.badge.setup': 'Vorbereitung',
    'videos.studio.badge.capture': 'Aufnahme',
    'videos.studio.badge.recording': 'Aufzeichnung',
    'videos.studio.badge.post': 'Schnitt',
    'videos.studio.cannotLeaveRecord': 'Beenden Sie die Aufnahme, bevor Sie den Schritt wechseln.',
    'videos.studio.railNeedRecording': 'Nehmen Sie ein Video auf, um den Schnitt-Schritt zu öffnen.',
    'videos.studio.sidebar.captureHelp':
      'Die Aufnahme erfasst den gesamten geteilten Stream (Bildschirm, Tab oder Fenster). Das Zuschneiden auf den nutzbaren Bereich erfolgt in der Schnitt-Phase.',
    'videos.studio.sidebar.sources': 'Quellen',
    'videos.studio.sidebar.recordingLive': 'Aufnahme läuft',
    'videos.studio.sidebar.finalizeHint':
      'Um in Ihrer Bibliothek zu speichern, nutzen Sie die Schaltfläche in der oberen Leiste.',
    'videos.studio.canvas.setupEmpty':
      'Lesen Sie das Seitenpanel, wechseln Sie dann zur Aufnahme.',
    'videos.studio.stepSetup': 'Vorbereitung',
    'videos.studio.stepCapture': 'Aufnahme',
    'videos.studio.stepPost': 'Schnitt',
    'videos.studio.titleLabel': 'Videotitel',
    'videos.studio.titlePlaceholder': 'Meine Aufnahme…',
    'videos.studio.saveSetup': 'Speichern',
    'videos.studio.allowMedia': 'Bildschirm und Mikrofon erlauben',
    'videos.studio.controlsHint':
      'Tipp: Um diese Leiste nicht mitaufzuzeichnen, teilen Sie einen Tab oder ein Fenster statt des ganzen Bildschirms oder nutzen Sie einen zweiten Monitor. Leertaste = Pause / Fortsetzen, wenn diese Seite fokussiert ist.',
    'videos.studio.controlsHintTab':
      'Sie teilen einen anderen Tab: Die Aufnahme zeigt diese Studio-Seite nicht (die Schaltflächen erscheinen daher nicht im Video). Halten Sie das Studio in einem anderen Browserfenster oder auf einem zweiten Bildschirm neben Ihrem Inhalt offen. Für Pause, Stopp oder Fertig wechseln Sie zum Fenster mit dem Studio (Alt+Tab bzw. Cmd+Tab). Die Leertaste wirkt nur, wenn die Studio-Seite fokussiert ist.',
    'videos.studio.controlsHintMonitor':
      'Sie teilen den ganzen Bildschirm: Diese Leiste kann in der Aufnahme sichtbar sein. Teilen Sie lieber einen Tab oder ein Fenster, oder verschieben Sie die Steuerung auf einen anderen Monitor.',
    'videos.studio.controlsHintWindow':
      'Sie teilen ein Fenster: Der restliche Desktop (andere Fenster) wird in der Regel nicht aufgezeichnet. Nutzen Sie die Schaltflächen unten für Pause, Stopp oder Fertig.',
    'videos.studio.startRecord': 'Aufnehmen',
    'videos.studio.pause': 'Pause',
    'videos.studio.resume': 'Fortsetzen',
    'videos.studio.stop': 'Stopp',
    'videos.studio.terminer': 'Fertig',
    'videos.studio.processingUpload': 'Hochladen und Transkription…',
    'videos.studio.timeline': 'Wiedergabe',
    'videos.studio.transcript': 'Erkannter Text',
    'videos.studio.voiceMode': 'Audiospur',
    'videos.studio.voiceOriginal': 'Meine Stimme (aufgenommenes Mikrofon)',
    'videos.studio.voiceAi': 'KI-Stimme (Mistral TTS)',
    'videos.studio.voiceSelect': 'Stimme',
    'videos.studio.polish': 'Text für KI-Stimme vorbereiten',
    'videos.studio.generateTts': 'Voiceover erzeugen',
    'videos.studio.applyVoiceover': 'KI-Stimme auf Video anwenden',
    'videos.studio.preview': 'Vorschau',
    'videos.studio.preview.noFile': 'Keine Videodatei zur Vorschau.',
    'videos.studio.bgImage': 'Hintergrundbild',
    'videos.studio.bgImageChoose': 'Auswählen',
    'videos.studio.bgImageInvalidType': 'Ungültiges Format. Verwenden Sie ein Bild (PNG, JPG, WebP).',
    'videos.studio.bgImageTooLarge': 'Das Bild darf maximal 10 MB groß sein.',
    'videos.studio.bgImageLoadError': 'Hintergrundbild konnte nicht geladen werden.',
    'videos.studio.bgImageUploading': 'Wird hochgeladen…',
    'videos.studio.bgImageUploadError': 'Bild konnte nicht hochgeladen werden.',
    'videos.studio.bgImageLibraryTitle': 'Video-Studio-Hintergrund',
    'videos.studio.postLayoutTitle': 'Rahmen & Hintergrund',
    'videos.studio.postLayoutHint':
      'Vorschau entspricht der veröffentlichten Datei: Entwurf speichern, dann veröffentlichen.',
    'videos.studio.bgImageVideoSize': 'Videogröße',
    'videos.studio.dragToMove': 'Ziehen zum Verschieben',
    'videos.studio.finalize': 'Speichern in Bibliothek',
    'videos.studio.back': 'Zurück zu Videos',
    'videos.studio.errorMedia': 'Bildschirm oder Mikrofon nicht zugänglich.',
    'videos.studio.errorInsecureContext':
      'Bildschirmaufnahme erfordert HTTPS oder localhost. Öffnen Sie die App über https:// oder http://127.0.0.1 / localhost.',
    'videos.studio.errorNotSupported':
      'Dieser Browser oder dieses Gerät unterstützt hier keine Bildschirmaufnahme. Nutzen Sie aktuelles Chrome oder Edge auf dem Desktop.',
    'videos.studio.errorMicDenied':
      'Mikrofon blockiert: Erlauben Sie das Mikrofon für diese Website (Schloss- oder Infosymbol in der Adressleiste), dann erneut versuchen.',
    'videos.studio.errorScreenDenied':
      'Bildschirmfreigabe abgelehnt: Wählen Sie im Dialog des Browsers einen Bildschirm oder Tab und bestätigen Sie „Teilen“.',
    'videos.studio.errorMicMissing':
      'Kein Mikrofon gefunden. Prüfen Sie Anschluss und Systemeinstellungen.',
    'videos.studio.errorScreenMissing': 'Bildschirmfreigabe auf diesem Gerät oder Browser nicht verfügbar.',
    'videos.studio.errorMicBusy':
      'Mikrofon wird von einer anderen App verwendet. Schließen Sie diese und versuchen Sie es erneut.',
    'videos.studio.errorScreenBusy':
      'Bildschirm konnte nicht erfasst werden (Ressource belegt). Erneut versuchen oder Browser neu starten.',
    'videos.studio.errorScreenCancelled': 'Bildschirmfreigabe abgebrochen.',
    'videos.studio.errorMicCancelled': 'Mikrofonzugriff abgebrochen.',
    'videos.studio.errorMicGeneric': 'Mikrofon nicht zugänglich.',
    'videos.studio.errorScreenGeneric': 'Bildschirm konnte nicht erfasst werden.',
    'videos.studio.errorScreenEnded': 'Bildschirmfreigabe beendet.',
    'videos.studio.mediaHints':
      'Chrome oder Edge auf dem Desktop. Bei http:// mit LAN-IP (z. B. 192.168.x.x) blockiert der Browser oft: nutzen Sie http://localhost:5173 oder gültiges HTTPS.',
    'videos.studio.errorRecord': 'Aufnahme in diesem Browser nicht unterstützt.',
    'videos.studio.errorUpload': 'Upload fehlgeschlagen.',
    'videos.studio.errorTranscribe': 'Transkription nicht verfügbar.',
    'videos.studio.errorPolish': 'KI-Textbearbeitung fehlgeschlagen.',
    'videos.studio.errorTts': 'Sprachsynthese fehlgeschlagen.',
    'videos.studio.errorMux': 'Audio-Zusammenführung fehlgeschlagen.',
    'videos.studio.muxSuccess': 'KI-Stimme auf Video angewendet',
    'videos.studio.successFinal': 'Video in Ihrer Bibliothek gespeichert',
    'videos.studio.audioTrackPlanned': 'Geplante Audiospur',
    'videos.studio.categoriesEmptyInstitution': 'Für Ihre Einrichtung sind keine Kategorien eingerichtet.',
    'videos.studio.categoriesHint': 'Eine oder mehrere Kategorien Ihrer Einrichtung (optional).',
    'videos.studio.crop': 'Zuschneiden',
    'videos.studio.cropActive': 'Zuschnitt aktiv — ziehen oder ändern Sie den Bereich auf dem Video',
    'videos.studio.cropApplied': 'Zuschnitt angewendet',
    'videos.studio.cropDone': 'Fertig',
    'videos.studio.cropEdit': 'Bearbeiten',
    'videos.studio.cropRemove': 'Entfernen',
    'videos.studio.cropReset': 'Zuschnitt zurücksetzen',
    'videos.studio.errorLoadDraft': 'Entwurf konnte nicht geladen werden.',
    'videos.studio.fieldCategories': 'Kategorien',
    'videos.studio.fieldDescription': 'Beschreibung',
    'videos.studio.generatingTimeline': 'Zeitleiste wird erstellt…',
    'videos.studio.postVoiceAiPipeline': 'KI-Stimme: Text vorbereiten, Audiospur erzeugen und auf das Video anwenden.',
    'videos.studio.postVoiceHumanKeep': 'Menschliche Stimme gewählt: Das Video behält den Mikrofon-Ton. Finalisieren Sie, wenn Sie bereit sind.',
    'videos.studio.processingPublish': 'Video wird verarbeitet…',
    'videos.studio.publish': 'Veröffentlichen',
    'videos.studio.saveDraft': 'Entwurf',
    'videos.studio.successDraft': 'Video als Entwurf gespeichert',
    'videos.studio.successPublish': 'Video in Ihrer Bibliothek veröffentlicht',
    'videos.studio.titleUntitled': 'Ohne Titel',
    'videos.studio.transcriptPlaceholder': 'Der erkannte Text Ihrer Aufnahme erscheint hier…',
    'videos.studio.transcriptRefining': 'Transkription wird verbessert…',
    'videos.studio.trimDuration': 'Dauer',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'Derzeit sind keine KI-Stimmen verfügbar.',
    'videos.studio.voiceAiPlan': 'KI-Stimme (Auswahl für den Schnitt)',
    'videos.studio.voiceHumanPlan': 'Menschliche Stimme (aufgenommenes Mikrofon)',
    'videos.studio.warnTabOnly': 'Sie haben einen einzelnen Tab ausgewählt: Nur dieser Tab wird aufgezeichnet, auch wenn Sie weitere öffnen. Um mehrere Tabs oder Fenster aufzuzeichnen, starten Sie die Aufnahme neu und wählen Sie „Gesamter Bildschirm“.',
    'videos.studio.templateMenuTitle': 'Vorlagenmenü',
    'videos.studio.templateEmpty': 'Keine gespeicherte Vorlage',
    'videos.studio.templatesHintCompact': 'Klicken Sie auf +, um eine Vorlage zu speichern, anzuwenden oder zu löschen.',
    'videos.studio.overlayAddMusic': 'Hintergrundmusik',
    'videos.studio.musicInvalidType': 'Ungültiges Format. Verwenden Sie Audio (MP3, WAV, M4A, OGG).',
    'videos.studio.musicTooLarge': 'Die Audiodatei darf maximal 50 MB groß sein.',
    'videos.studio.musicDefault': 'Musik',
    'videos.studio.musicAdded': 'Hintergrundmusik zur Timeline hinzugefügt.',
    'videos.studio.musicUploadError': 'Hintergrundmusik konnte nicht hinzugefügt werden.',
    'videos.studio.layerScreenBorder': 'Außenrahmen',
    'videos.studio.layerScreenBorderSize': 'Größe',
    'videos.studio.layerScreenBorderColor': 'Farbe',
    'videos.studio.layerScreenBorderRadius': 'Eckenrundung',
    'videos.studio.segmentToolsTitle': 'Segment-Werkzeuge',
    'videos.studio.segmentZoom': 'Zoom',
    'videos.studio.segmentResetView': 'Ansicht zurücksetzen',
    'videos.studio.segmentTransition': 'Übergang',
    'videos.studio.transitionNone': 'Keine',
    'videos.studio.transitionFade': 'Überblenden',
    'videos.studio.transitionWipeLeft': 'Wisch →',
    'videos.studio.transitionSlideUp': 'Schieben ↑',
    'videos.studio.textEditor.toolbar': 'Textformatierung',
    'videos.studio.textEditor.bold': 'Fett',
    'videos.studio.textEditor.italic': 'Kursiv',
    'videos.studio.textEditor.underline': 'Unterstrichen',
    'videos.studio.textEditor.textColor': 'Textfarbe (Auswahl)',
    'videos.studio.textEditor.size': 'Größe',
    'videos.studio.textEditor.font': 'Schriftart',
    'videos.studio.textEditor.fontFamily.system': 'System',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
  es: {
    'videos.page.studioButton': 'Estudio',
    'videos.studio.title': 'Estudio de vídeo',
    'videos.studio.rail.prep': 'Prep.',
    'videos.studio.rail.capture': 'Captura',
    'videos.studio.rail.post': 'Montaje',
    'videos.studio.rail.aria': 'Pasos del estudio de vídeo',
    'videos.studio.badge.setup': 'Preparación',
    'videos.studio.badge.capture': 'Captura',
    'videos.studio.badge.recording': 'Grabación',
    'videos.studio.badge.post': 'Montaje',
    'videos.studio.cannotLeaveRecord': 'Detenga la grabación antes de cambiar de paso.',
    'videos.studio.railNeedRecording': 'Grabe un vídeo para abrir el paso de montaje.',
    'videos.studio.sidebar.captureHelp':
      'La grabación capta todo el flujo compartido (pantalla, pestaña o ventana). El recorte al área útil se hará en la fase de montaje.',
    'videos.studio.sidebar.sources': 'Fuentes',
    'videos.studio.sidebar.recordingLive': 'Grabación en curso',
    'videos.studio.sidebar.finalizeHint':
      'Para guardar en su biblioteca, use el botón de la barra superior.',
    'videos.studio.canvas.setupEmpty':
      'Lea el panel lateral y pase al paso Captura.',
    'videos.studio.stepSetup': 'Preparación',
    'videos.studio.stepCapture': 'Captura',
    'videos.studio.stepPost': 'Montaje',
    'videos.studio.titleLabel': 'Título del vídeo',
    'videos.studio.titlePlaceholder': 'Mi grabación…',
    'videos.studio.saveSetup': 'Guardar',
    'videos.studio.allowMedia': 'Permitir pantalla y micrófono',
    'videos.studio.controlsHint':
      'Consejo: para no grabar esta barra, comparta una pestaña o una ventana en lugar de toda la pantalla, o use un segundo monitor. Barra espaciadora = pausa / reanudar cuando esta página tiene el foco.',
    'videos.studio.controlsHintTab':
      'Está compartiendo otra pestaña: la grabación no incluye esta página del estudio (los botones no aparecen en el vídeo). Mantenga el estudio en otra ventana del navegador o en un segundo monitor junto a su contenido. Para Pausa, Detener o Terminar, cambie a la ventana donde está el estudio (Alt+Tab o Cmd+Tab). La barra espaciadora solo funciona con el foco en la página del estudio.',
    'videos.studio.controlsHintMonitor':
      'Está compartiendo toda la pantalla: esta barra puede aparecer en la grabación. Prefiera «Pestaña» o «Ventana» en el uso compartido, o mueva los controles a otro monitor.',
    'videos.studio.controlsHintWindow':
      'Está compartiendo una ventana: el resto del escritorio (y otras ventanas) normalmente no se graba. Use los botones de abajo para Pausa, Detener o Terminar.',
    'videos.studio.startRecord': 'Grabar',
    'videos.studio.pause': 'Pausa',
    'videos.studio.resume': 'Reanudar',
    'videos.studio.stop': 'Detener',
    'videos.studio.terminer': 'Terminar',
    'videos.studio.processingUpload': 'Subida y transcripción…',
    'videos.studio.timeline': 'Reproducción',
    'videos.studio.transcript': 'Texto reconocido',
    'videos.studio.voiceMode': 'Pista de audio',
    'videos.studio.voiceOriginal': 'Mi voz (micrófono grabado)',
    'videos.studio.voiceAi': 'Voz IA (Mistral TTS)',
    'videos.studio.voiceSelect': 'Voz',
    'videos.studio.polish': 'Preparar el texto para la voz IA',
    'videos.studio.generateTts': 'Generar voz en off',
    'videos.studio.applyVoiceover': 'Aplicar voz IA al vídeo',
    'videos.studio.preview': 'Vista previa',
    'videos.studio.preview.noFile': 'No hay archivo de vídeo para previsualizar.',
    'videos.studio.bgImage': 'Imagen de fondo',
    'videos.studio.bgImageChoose': 'Elegir',
    'videos.studio.bgImageInvalidType': 'Formato no válido. Utilice una imagen (PNG, JPG, WebP).',
    'videos.studio.bgImageTooLarge': 'La imagen no debe superar los 10 MB.',
    'videos.studio.bgImageLoadError': 'No se pudo cargar la imagen de fondo.',
    'videos.studio.bgImageVideoSize': 'Tamaño del vídeo',
    'videos.studio.dragToMove': 'Arrastrar para mover',
    'videos.studio.finalize': 'Guardar en biblioteca',
    'videos.studio.back': 'Volver a vídeos',
    'videos.studio.errorMedia': 'No se puede acceder a la pantalla o al micrófono.',
    'videos.studio.errorInsecureContext':
      'La captura requiere HTTPS o localhost. Abra la aplicación con https:// o http://127.0.0.1 / localhost.',
    'videos.studio.errorNotSupported':
      'Este navegador o dispositivo no admite la captura de pantalla aquí. Use Chrome o Edge actualizado en escritorio.',
    'videos.studio.errorMicDenied':
      'Micrófono bloqueado: permita el micrófono para este sitio (candado o ⓘ en la barra de direcciones) e inténtelo de nuevo.',
    'videos.studio.errorScreenDenied':
      'Compartir pantalla denegado: elija una pantalla o pestaña en el cuadro del navegador y confirme «Compartir».',
    'videos.studio.errorMicMissing':
      'No se detectó micrófono. Compruebe el hardware y la configuración del sistema.',
    'videos.studio.errorScreenMissing': 'Compartir pantalla no disponible en este dispositivo o navegador.',
    'videos.studio.errorMicBusy':
      'El micrófono está en uso por otra aplicación. Ciérrela e inténtelo de nuevo.',
    'videos.studio.errorScreenBusy':
      'No se pudo capturar la pantalla (recurso ocupado). Reintente o reinicie el navegador.',
    'videos.studio.errorScreenCancelled': 'Compartir pantalla cancelado.',
    'videos.studio.errorMicCancelled': 'Acceso al micrófono cancelado.',
    'videos.studio.errorMicGeneric': 'No se puede acceder al micrófono.',
    'videos.studio.errorScreenGeneric': 'No se puede capturar la pantalla.',
    'videos.studio.errorScreenEnded': 'Compartir pantalla detenido.',
    'videos.studio.mediaHints':
      'Chrome o Edge en escritorio. Si la URL es http:// con una IP de red local (p. ej. 192.168.x.x), el navegador suele bloquear: use http://localhost:5173 o HTTPS válido.',
    'videos.studio.errorRecord': 'La grabación no es compatible con este navegador.',
    'videos.studio.errorUpload': 'Error al subir.',
    'videos.studio.errorTranscribe': 'Transcripción no disponible.',
    'videos.studio.errorPolish': 'Falló el pulido de texto con IA.',
    'videos.studio.errorTts': 'Falló la síntesis de voz.',
    'videos.studio.errorMux': 'Falló la mezcla de audio.',
    'videos.studio.muxSuccess': 'Voz IA aplicada al vídeo',
    'videos.studio.successFinal': 'Vídeo guardado en su biblioteca',
    'videos.studio.audioTrackPlanned': 'Pista de audio prevista',
    'videos.studio.categoriesEmptyInstitution': 'No hay categorías configuradas para su institución.',
    'videos.studio.categoriesHint': 'Una o más categorías de su institución (opcional).',
    'videos.studio.crop': 'Recortar',
    'videos.studio.cropActive': 'Recorte activo — arrastre o redimensione la zona en el vídeo',
    'videos.studio.cropApplied': 'Recorte aplicado',
    'videos.studio.cropDone': 'Listo',
    'videos.studio.cropEdit': 'Editar',
    'videos.studio.cropRemove': 'Eliminar',
    'videos.studio.cropReset': 'Restablecer recorte',
    'videos.studio.errorLoadDraft': 'No se pudo cargar el borrador.',
    'videos.studio.fieldCategories': 'Categorías',
    'videos.studio.fieldDescription': 'Descripción',
    'videos.studio.generatingTimeline': 'Generando línea de tiempo…',
    'videos.studio.postVoiceAiPipeline': 'Voz IA: prepare el texto, genere la pista de audio y aplíquela al vídeo.',
    'videos.studio.postVoiceHumanKeep': 'Ha elegido voz humana: el vídeo conserva el audio del micrófono. Finalice cuando esté listo.',
    'videos.studio.processingPublish': 'Procesando vídeo…',
    'videos.studio.publish': 'Publicar',
    'videos.studio.saveDraft': 'Borrador',
    'videos.studio.successDraft': 'Vídeo guardado como borrador',
    'videos.studio.successPublish': 'Vídeo publicado en su biblioteca',
    'videos.studio.titleUntitled': 'Sin título',
    'videos.studio.transcriptPlaceholder': 'El texto reconocido de su grabación aparecerá aquí…',
    'videos.studio.transcriptRefining': 'Mejorando la transcripción…',
    'videos.studio.trimDuration': 'duración',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'No hay voces IA disponibles en este momento.',
    'videos.studio.voiceAiPlan': 'Voz IA (elección para el montaje)',
    'videos.studio.voiceHumanPlan': 'Voz humana (micrófono grabado)',
    'videos.studio.warnTabOnly': 'Ha seleccionado una sola pestaña: solo se grabará esa pestaña, aunque abra otras. Para capturar varias pestañas o ventanas, reinicie la captura y elija «Pantalla completa».',
    'videos.studio.templateMenuTitle': 'Menú de plantillas',
    'videos.studio.templateEmpty': 'No hay plantillas guardadas',
    'videos.studio.templatesHintCompact': 'Haz clic en + para guardar, aplicar o eliminar una plantilla.',
    'videos.studio.overlayAddMusic': 'Música de fondo',
    'videos.studio.musicInvalidType': 'Formato no válido. Use audio (MP3, WAV, M4A, OGG).',
    'videos.studio.musicTooLarge': 'El archivo de audio no debe superar los 50 MB.',
    'videos.studio.musicDefault': 'Música',
    'videos.studio.musicAdded': 'Música de fondo añadida a la línea de tiempo.',
    'videos.studio.musicUploadError': 'No se pudo añadir la música de fondo.',
    'videos.studio.layerScreenBorder': 'Borde exterior',
    'videos.studio.layerScreenBorderSize': 'Tamaño',
    'videos.studio.layerScreenBorderColor': 'Color',
    'videos.studio.layerScreenBorderRadius': 'Redondeo de esquinas',
    'videos.studio.segmentToolsTitle': 'Herramientas de segmento',
    'videos.studio.segmentZoom': 'Zoom',
    'videos.studio.segmentResetView': 'Reiniciar vista',
    'videos.studio.segmentTransition': 'Transición',
    'videos.studio.transitionNone': 'Ninguna',
    'videos.studio.transitionFade': 'Fundido',
    'videos.studio.transitionWipeLeft': 'Barrido →',
    'videos.studio.transitionSlideUp': 'Deslizar ↑',
    'videos.studio.textEditor.toolbar': 'Formato de texto',
    'videos.studio.textEditor.bold': 'Negrita',
    'videos.studio.textEditor.italic': 'Cursiva',
    'videos.studio.textEditor.underline': 'Subrayado',
    'videos.studio.textEditor.textColor': 'Color del texto (selección)',
    'videos.studio.textEditor.size': 'Tamaño',
    'videos.studio.textEditor.font': 'Fuente',
    'videos.studio.textEditor.fontFamily.system': 'Sistema',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
  it: {
    'videos.page.studioButton': 'Studio',
    'videos.studio.title': 'Studio video',
    'videos.studio.rail.prep': 'Prep.',
    'videos.studio.rail.capture': 'Registrazione',
    'videos.studio.rail.post': 'Montaggio',
    'videos.studio.rail.aria': 'Fasi dello studio video',
    'videos.studio.badge.setup': 'Preparazione',
    'videos.studio.badge.capture': 'Cattura',
    'videos.studio.badge.recording': 'Registrazione',
    'videos.studio.badge.post': 'Montaggio',
    'videos.studio.cannotLeaveRecord': 'Interrompete la registrazione prima di cambiare fase.',
    'videos.studio.railNeedRecording': 'Registrate un video per aprire la fase di montaggio.',
    'videos.studio.sidebar.captureHelp':
      'La registrazione acquisisce l’intero flusso condiviso (schermo, scheda o finestra). Il ritaglio dell’area utile si farà nella fase Montaggio.',
    'videos.studio.sidebar.sources': 'Sorgenti',
    'videos.studio.sidebar.recordingLive': 'Registrazione in corso',
    'videos.studio.sidebar.finalizeHint':
      'Per salvare in biblioteca, usate il pulsante nella barra superiore.',
    'videos.studio.canvas.setupEmpty':
      'Leggete il pannello laterale, poi passate alla fase Cattura.',
    'videos.studio.stepSetup': 'Preparazione',
    'videos.studio.stepCapture': 'Cattura',
    'videos.studio.stepPost': 'Montaggio',
    'videos.studio.titleLabel': 'Titolo del video',
    'videos.studio.titlePlaceholder': 'La mia registrazione…',
    'videos.studio.saveSetup': 'Salva',
    'videos.studio.allowMedia': 'Consenti schermo e microfono',
    'videos.studio.controlsHint':
      'Suggerimento: per non registrare questa barra, condividete una scheda o una finestra invece dell’intero schermo, oppure usate un secondo monitor. Barra spaziatrice = pausa / ripresa quando questa pagina ha il focus.',
    'videos.studio.controlsHintTab':
      'State condividendo un’altra scheda: la registrazione non include questa pagina Studio (i pulsanti non compaiono nel video). Tenete lo Studio in un’altra finestra del browser o su un secondo schermo accanto al contenuto. Per Pausa, Stop o Fine, passate alla finestra in cui è aperto lo Studio (Alt+Tab o Cmd+Tab). La barra spaziatrice funziona solo con il focus sulla pagina Studio.',
    'videos.studio.controlsHintMonitor':
      'State condividendo l’intero schermo: questa barra può apparire nella registrazione. Preferite «Scheda» o «Finestra» nella condivisione, oppure spostate i controlli su un altro monitor.',
    'videos.studio.controlsHintWindow':
      'State condividendo una finestra: il resto del desktop (e le altre finestre) di solito non viene registrato. Usate i pulsanti sotto per Pausa, Stop o Fine.',
    'videos.studio.startRecord': 'Registra',
    'videos.studio.pause': 'Pausa',
    'videos.studio.resume': 'Riprendi',
    'videos.studio.stop': 'Stop',
    'videos.studio.terminer': 'Fine',
    'videos.studio.processingUpload': 'Caricamento e trascrizione…',
    'videos.studio.timeline': 'Riproduzione',
    'videos.studio.transcript': 'Testo riconosciuto',
    'videos.studio.voiceMode': 'Traccia audio',
    'videos.studio.voiceOriginal': 'La mia voce (microfono registrato)',
    'videos.studio.voiceAi': 'Voce IA (Mistral TTS)',
    'videos.studio.voiceSelect': 'Voce',
    'videos.studio.polish': 'Prepara il testo per la voce IA',
    'videos.studio.generateTts': 'Genera voice-over',
    'videos.studio.applyVoiceover': 'Applica voce IA al video',
    'videos.studio.preview': 'Anteprima',
    'videos.studio.preview.noFile': 'Nessun file video da visualizzare.',
    'videos.studio.bgImage': 'Immagine di sfondo',
    'videos.studio.bgImageChoose': 'Scegliere',
    'videos.studio.bgImageInvalidType': 'Formato non valido. Usare un\'immagine (PNG, JPG, WebP).',
    'videos.studio.bgImageTooLarge': "L'immagine non deve superare 10 MB.",
    'videos.studio.bgImageLoadError': 'Impossibile caricare l’immagine di sfondo.',
    'videos.studio.bgImageVideoSize': 'Dimensione video',
    'videos.studio.dragToMove': 'Trascinare per spostare',
    'videos.studio.finalize': 'Salva in biblioteca',
    'videos.studio.back': 'Torna ai video',
    'videos.studio.errorMedia': 'Impossibile accedere allo schermo o al microfono.',
    'videos.studio.errorInsecureContext':
      'La cattura richiede HTTPS o localhost. Aprite l’app con https:// o http://127.0.0.1 / localhost.',
    'videos.studio.errorNotSupported':
      'Questo browser o dispositivo non supporta la cattura dello schermo qui. Usate Chrome o Edge aggiornato su desktop.',
    'videos.studio.errorMicDenied':
      'Microfono bloccato: consentite il microfono per questo sito (lucchetto o ⓘ nella barra degli indirizzi), poi riprovate.',
    'videos.studio.errorScreenDenied':
      'Condivisione schermo rifiutata: scegliete uno schermo o una scheda nella finestra del browser e confermate «Condividi».',
    'videos.studio.errorMicMissing':
      'Nessun microfono rilevato. Controllate collegamenti e impostazioni di sistema.',
    'videos.studio.errorScreenMissing': 'Condivisione schermo non disponibile su questo dispositivo o browser.',
    'videos.studio.errorMicBusy':
      'Microfono in uso da un’altra app. Chiudetela e riprovate.',
    'videos.studio.errorScreenBusy':
      'Impossibile catturare lo schermo (risorsa occupata). Riprovate o riavviate il browser.',
    'videos.studio.errorScreenCancelled': 'Condivisione schermo annullata.',
    'videos.studio.errorMicCancelled': 'Accesso al microfono annullato.',
    'videos.studio.errorMicGeneric': 'Impossibile accedere al microfono.',
    'videos.studio.errorScreenGeneric': 'Impossibile catturare lo schermo.',
    'videos.studio.errorScreenEnded': 'Condivisione schermo interrotta.',
    'videos.studio.mediaHints':
      'Chrome o Edge su desktop. Con http:// e un IP LAN (es. 192.168.x.x) il browser spesso blocca: usate http://localhost:5173 o HTTPS valido.',
    'videos.studio.errorRecord': 'Registrazione non supportata in questo browser.',
    'videos.studio.errorUpload': 'Caricamento non riuscito.',
    'videos.studio.errorTranscribe': 'Trascrizione non disponibile.',
    'videos.studio.errorPolish': 'Rifinitura testo IA non riuscita.',
    'videos.studio.errorTts': 'Sintesi vocale non riuscita.',
    'videos.studio.errorMux': 'Unione audio non riuscita.',
    'videos.studio.muxSuccess': 'Voce IA applicata al video',
    'videos.studio.successFinal': 'Video salvato nella biblioteca',
    'videos.studio.audioTrackPlanned': 'Traccia audio prevista',
    'videos.studio.categoriesEmptyInstitution': 'Non sono configurate categorie per il vostro ente.',
    'videos.studio.categoriesHint': 'Una o più categorie del vostro ente (facoltativo).',
    'videos.studio.crop': 'Ritaglia',
    'videos.studio.cropActive': 'Ritaglio attivo — trascina o ridimensiona l\'area sul video',
    'videos.studio.cropApplied': 'Ritaglio applicato',
    'videos.studio.cropDone': 'Fatto',
    'videos.studio.cropEdit': 'Modifica',
    'videos.studio.cropRemove': 'Rimuovi',
    'videos.studio.cropReset': 'Reimposta ritaglio',
    'videos.studio.errorLoadDraft': 'Impossibile caricare la bozza.',
    'videos.studio.fieldCategories': 'Categorie',
    'videos.studio.fieldDescription': 'Descrizione',
    'videos.studio.generatingTimeline': 'Generazione della timeline…',
    'videos.studio.postVoiceAiPipeline': 'Voce IA: preparare il testo, generare la traccia audio e applicarla al video.',
    'videos.studio.postVoiceHumanKeep': 'Avete scelto la voce umana: il video conserva l\'audio del microfono. Finalizzate quando siete pronti.',
    'videos.studio.processingPublish': 'Elaborazione video in corso…',
    'videos.studio.publish': 'Pubblica',
    'videos.studio.saveDraft': 'Bozza',
    'videos.studio.successDraft': 'Video salvato come bozza',
    'videos.studio.successPublish': 'Video pubblicato nella vostra libreria',
    'videos.studio.titleUntitled': 'Senza titolo',
    'videos.studio.transcriptPlaceholder': 'Il testo riconosciuto dalla registrazione apparirà qui…',
    'videos.studio.transcriptRefining': 'Miglioramento della trascrizione…',
    'videos.studio.trimDuration': 'durata',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'Nessuna voce IA disponibile al momento.',
    'videos.studio.voiceAiPlan': 'Voce IA (scelta per il montaggio)',
    'videos.studio.voiceHumanPlan': 'Voce umana (microfono registrato)',
    'videos.studio.warnTabOnly': 'Avete selezionato una singola scheda: verrà registrata solo quella, anche se ne aprite altre. Per catturare più schede o finestre, riavviate la cattura e scegliete «Schermo intero».',
    'videos.studio.templateMenuTitle': 'Menu modelli',
    'videos.studio.templateEmpty': 'Nessun modello salvato',
    'videos.studio.templatesHintCompact': 'Clicca su + per salvare, applicare o eliminare un modello.',
    'videos.studio.overlayAddMusic': 'Musica di sottofondo',
    'videos.studio.musicInvalidType': 'Formato non valido. Usa audio (MP3, WAV, M4A, OGG).',
    'videos.studio.musicTooLarge': 'Il file audio non deve superare 50 MB.',
    'videos.studio.musicDefault': 'Musica',
    'videos.studio.musicAdded': 'Musica di sottofondo aggiunta alla timeline.',
    'videos.studio.musicUploadError': 'Impossibile aggiungere la musica di sottofondo.',
    'videos.studio.layerScreenBorder': 'Bordo esterno',
    'videos.studio.layerScreenBorderSize': 'Dimensione',
    'videos.studio.layerScreenBorderColor': 'Colore',
    'videos.studio.layerScreenBorderRadius': 'Arrotondamento angoli',
    'videos.studio.segmentToolsTitle': 'Strumenti segmento',
    'videos.studio.segmentZoom': 'Zoom',
    'videos.studio.segmentResetView': 'Reimposta vista',
    'videos.studio.segmentTransition': 'Transizione',
    'videos.studio.transitionNone': 'Nessuna',
    'videos.studio.transitionFade': 'Dissolvenza',
    'videos.studio.transitionWipeLeft': 'Scorrimento →',
    'videos.studio.transitionSlideUp': 'Scorrimento ↑',
    'videos.studio.textEditor.toolbar': 'Formattazione testo',
    'videos.studio.textEditor.bold': 'Grassetto',
    'videos.studio.textEditor.italic': 'Corsivo',
    'videos.studio.textEditor.underline': 'Sottolineato',
    'videos.studio.textEditor.textColor': 'Colore del testo (selezione)',
    'videos.studio.textEditor.size': 'Dimensione',
    'videos.studio.textEditor.font': 'Carattere',
    'videos.studio.textEditor.fontFamily.system': 'Sistema',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
  pt: {
    'videos.page.studioButton': 'Estúdio',
    'videos.studio.title': 'Estúdio de vídeo',
    'videos.studio.rail.prep': 'Prep.',
    'videos.studio.rail.capture': 'Captura',
    'videos.studio.rail.post': 'Edição',
    'videos.studio.rail.aria': 'Etapas do estúdio de vídeo',
    'videos.studio.badge.setup': 'Preparação',
    'videos.studio.badge.capture': 'Captura',
    'videos.studio.badge.recording': 'Gravação',
    'videos.studio.badge.post': 'Edição',
    'videos.studio.cannotLeaveRecord': 'Pare a gravação antes de mudar de etapa.',
    'videos.studio.railNeedRecording': 'Grave um vídeo para abrir a etapa de edição.',
    'videos.studio.sidebar.captureHelp':
      'A gravação capta todo o fluxo partilhado (ecrã, separador ou janela). O recorte à área útil far-se-á na fase de edição.',
    'videos.studio.sidebar.sources': 'Fontes',
    'videos.studio.sidebar.recordingLive': 'Gravação em curso',
    'videos.studio.sidebar.finalizeHint':
      'Para guardar na biblioteca, use o botão na barra superior.',
    'videos.studio.canvas.setupEmpty':
      'Leia o painel lateral e passe à etapa Captura.',
    'videos.studio.stepSetup': 'Preparação',
    'videos.studio.stepCapture': 'Captura',
    'videos.studio.stepPost': 'Edição',
    'videos.studio.titleLabel': 'Título do vídeo',
    'videos.studio.titlePlaceholder': 'A minha gravação…',
    'videos.studio.saveSetup': 'Guardar',
    'videos.studio.allowMedia': 'Permitir ecrã e microfone',
    'videos.studio.controlsHint':
      'Dica: para não gravar esta barra, partilhe um separador ou uma janela em vez de todo o ecrã, ou use um segundo monitor. Barra de espaços = pausa / retomar quando esta página está em foco.',
    'videos.studio.controlsHintTab':
      'Está a partilhar outro separador: a gravação não inclui esta página do estúdio (os botões não aparecem no vídeo). Mantenha o estúdio noutra janela do browser ou num segundo ecrã junto ao conteúdo. Para Pausa, Parar ou Concluir, mude para a janela onde o estúdio está aberto (Alt+Tab ou Cmd+Tab). A barra de espaços só funciona com foco na página do estúdio.',
    'videos.studio.controlsHintMonitor':
      'Está a partilhar o ecrã inteiro: esta barra pode aparecer na gravação. Prefira «Separador» ou «Janela» na partilha, ou mova os controlos para outro monitor.',
    'videos.studio.controlsHintWindow':
      'Está a partilhar uma janela: o resto do ambiente de trabalho (outras janelas) normalmente não é gravado. Use os botões abaixo para Pausa, Parar ou Concluir.',
    'videos.studio.startRecord': 'Gravar',
    'videos.studio.pause': 'Pausa',
    'videos.studio.resume': 'Retomar',
    'videos.studio.stop': 'Parar',
    'videos.studio.terminer': 'Concluir',
    'videos.studio.processingUpload': 'Envio e transcrição…',
    'videos.studio.timeline': 'Reprodução',
    'videos.studio.transcript': 'Texto reconhecido',
    'videos.studio.voiceMode': 'Faixa de áudio',
    'videos.studio.voiceOriginal': 'A minha voz (microfone gravado)',
    'videos.studio.voiceAi': 'Voz IA (Mistral TTS)',
    'videos.studio.voiceSelect': 'Voz',
    'videos.studio.polish': 'Preparar o texto para a voz IA',
    'videos.studio.generateTts': 'Gerar locução',
    'videos.studio.applyVoiceover': 'Aplicar voz IA ao vídeo',
    'videos.studio.preview': 'Pré-visualizar',
    'videos.studio.preview.noFile': 'Nenhum ficheiro de vídeo para pré-visualizar.',
    'videos.studio.bgImage': 'Imagem de fundo',
    'videos.studio.bgImageChoose': 'Escolher',
    'videos.studio.bgImageInvalidType': 'Formato inválido. Utilize uma imagem (PNG, JPG, WebP).',
    'videos.studio.bgImageTooLarge': 'A imagem não deve exceder 10 MB.',
    'videos.studio.bgImageLoadError': 'Não foi possível carregar a imagem de fundo.',
    'videos.studio.bgImageVideoSize': 'Tamanho do vídeo',
    'videos.studio.dragToMove': 'Arrastar para mover',
    'videos.studio.finalize': 'Guardar na biblioteca',
    'videos.studio.back': 'Voltar aos vídeos',
    'videos.studio.errorMedia': 'Não foi possível aceder ao ecrã ou ao microfone.',
    'videos.studio.errorInsecureContext':
      'A captura requer HTTPS ou localhost. Abra a aplicação com https:// ou http://127.0.0.1 / localhost.',
    'videos.studio.errorNotSupported':
      'Este browser ou dispositivo não suporta captura de ecrã aqui. Use Chrome ou Edge atualizado em desktop.',
    'videos.studio.errorMicDenied':
      'Microfone bloqueado: permita o microfone para este site (cadeado ou ⓘ na barra de endereço) e tente novamente.',
    'videos.studio.errorScreenDenied':
      'Partilha de ecrã recusada: escolha um ecrã ou separador na janela do browser e confirme «Partilhar».',
    'videos.studio.errorMicMissing':
      'Nenhum microfone detetado. Verifique hardware e definições do sistema.',
    'videos.studio.errorScreenMissing': 'Partilha de ecrã indisponível neste dispositivo ou browser.',
    'videos.studio.errorMicBusy':
      'Microfone em uso por outra aplicação. Feche-a e tente novamente.',
    'videos.studio.errorScreenBusy':
      'Não foi possível capturar o ecrã (recurso ocupado). Tente de novo ou reinicie o browser.',
    'videos.studio.errorScreenCancelled': 'Partilha de ecrã cancelada.',
    'videos.studio.errorMicCancelled': 'Acesso ao microfone cancelado.',
    'videos.studio.errorMicGeneric': 'Não foi possível aceder ao microfone.',
    'videos.studio.errorScreenGeneric': 'Não foi possível capturar o ecrã.',
    'videos.studio.errorScreenEnded': 'Partilha de ecrã terminada.',
    'videos.studio.mediaHints':
      'Chrome ou Edge em desktop. Com http:// e IP de rede local (ex. 192.168.x.x), o browser costuma bloquear: use http://localhost:5173 ou HTTPS válido.',
    'videos.studio.errorRecord': 'Gravação não suportada neste browser.',
    'videos.studio.errorUpload': 'Falha no envio.',
    'videos.studio.errorTranscribe': 'Transcrição indisponível.',
    'videos.studio.errorPolish': 'Falha no polimento de texto IA.',
    'videos.studio.errorTts': 'Falha na síntese de voz.',
    'videos.studio.errorMux': 'Falha na mistura de áudio.',
    'videos.studio.muxSuccess': 'Voz IA aplicada ao vídeo',
    'videos.studio.successFinal': 'Vídeo guardado na biblioteca',
    'videos.studio.audioTrackPlanned': 'Faixa de áudio prevista',
    'videos.studio.categoriesEmptyInstitution': 'Não existem categorias configuradas para a sua instituição.',
    'videos.studio.categoriesHint': 'Uma ou mais categorias da sua instituição (opcional).',
    'videos.studio.crop': 'Recortar',
    'videos.studio.cropActive': 'Recorte ativo — arraste ou redimensione a zona no vídeo',
    'videos.studio.cropApplied': 'Recorte aplicado',
    'videos.studio.cropDone': 'Concluído',
    'videos.studio.cropEdit': 'Editar',
    'videos.studio.cropRemove': 'Remover',
    'videos.studio.cropReset': 'Repor recorte',
    'videos.studio.errorLoadDraft': 'Não foi possível carregar o rascunho.',
    'videos.studio.fieldCategories': 'Categorias',
    'videos.studio.fieldDescription': 'Descrição',
    'videos.studio.generatingTimeline': 'A gerar linha do tempo…',
    'videos.studio.postVoiceAiPipeline': 'Voz IA: prepare o texto, gere a faixa de áudio e aplique-a ao vídeo.',
    'videos.studio.postVoiceHumanKeep': 'Escolheu voz humana: o vídeo conserva o áudio do microfone. Finalize quando estiver pronto.',
    'videos.studio.processingPublish': 'A processar vídeo…',
    'videos.studio.publish': 'Publicar',
    'videos.studio.saveDraft': 'Rascunho',
    'videos.studio.successDraft': 'Vídeo guardado como rascunho',
    'videos.studio.successPublish': 'Vídeo publicado na sua biblioteca',
    'videos.studio.titleUntitled': 'Sem título',
    'videos.studio.transcriptPlaceholder': 'O texto reconhecido da sua gravação aparecerá aqui…',
    'videos.studio.transcriptRefining': 'A melhorar a transcrição…',
    'videos.studio.trimDuration': 'duração',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'Nenhuma voz IA disponível de momento.',
    'videos.studio.voiceAiPlan': 'Voz IA (escolha para a edição)',
    'videos.studio.voiceHumanPlan': 'Voz humana (microfone gravado)',
    'videos.studio.warnTabOnly': 'Selecionou um único separador: apenas esse separador será gravado, mesmo que abra outros. Para capturar vários separadores ou janelas, reinicie a captura e escolha «Ecrã inteiro».',
    'videos.studio.templateMenuTitle': 'Menu de modelos',
    'videos.studio.templateEmpty': 'Nenhum modelo guardado',
    'videos.studio.templatesHintCompact': 'Clique em + para guardar, aplicar ou eliminar um modelo.',
    'videos.studio.overlayAddMusic': 'Música de fundo',
    'videos.studio.musicInvalidType': 'Formato inválido. Use áudio (MP3, WAV, M4A, OGG).',
    'videos.studio.musicTooLarge': 'O ficheiro de áudio não deve ultrapassar 50 MB.',
    'videos.studio.musicDefault': 'Música',
    'videos.studio.musicAdded': 'Música de fundo adicionada à timeline.',
    'videos.studio.musicUploadError': 'Não foi possível adicionar a música de fundo.',
    'videos.studio.layerScreenBorder': 'Borda exterior',
    'videos.studio.layerScreenBorderSize': 'Tamanho',
    'videos.studio.layerScreenBorderColor': 'Cor',
    'videos.studio.layerScreenBorderRadius': 'Arredondamento dos cantos',
    'videos.studio.segmentToolsTitle': 'Ferramentas do segmento',
    'videos.studio.segmentZoom': 'Zoom',
    'videos.studio.segmentResetView': 'Repor vista',
    'videos.studio.segmentTransition': 'Transição',
    'videos.studio.transitionNone': 'Nenhuma',
    'videos.studio.transitionFade': 'Fade',
    'videos.studio.transitionWipeLeft': 'Varrimento →',
    'videos.studio.transitionSlideUp': 'Deslize ↑',
    'videos.studio.textEditor.toolbar': 'Formatação de texto',
    'videos.studio.textEditor.bold': 'Negrito',
    'videos.studio.textEditor.italic': 'Itálico',
    'videos.studio.textEditor.underline': 'Sublinhado',
    'videos.studio.textEditor.textColor': 'Cor do texto (seleção)',
    'videos.studio.textEditor.size': 'Tamanho',
    'videos.studio.textEditor.font': 'Fonte',
    'videos.studio.textEditor.fontFamily.system': 'Sistema',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
  lb: {
    'videos.page.studioButton': 'Studio',
    'videos.studio.title': 'Video-Studio',
    'videos.studio.rail.prep': 'Virbereedung',
    'videos.studio.rail.capture': 'Opnam',
    'videos.studio.rail.post': 'Montage',
    'videos.studio.rail.aria': 'Video-Studio Schrëtt',
    'videos.studio.badge.setup': 'Virbereedung',
    'videos.studio.badge.capture': 'Opnam',
    'videos.studio.badge.recording': 'Opnam leeft',
    'videos.studio.badge.post': 'Montage',
    'videos.studio.cannotLeaveRecord': 'Stoppt d’Opnam, éier Dir de Schrëtt wiesselt.',
    'videos.studio.railNeedRecording': 'Huelt e Video op, fir de Montage-Schrëtt opzemaachen.',
    'videos.studio.sidebar.captureHelp':
      "D'Opnam hëlt de ganzen gedeelte Stream (Ecran, Tab oder Fënster). D'Zuschneiden op de nëtzleche Beräich geschitt an der Montage-Phas.",
    'videos.studio.sidebar.sources': 'Quellen',
    'videos.studio.sidebar.recordingLive': 'Opnam leeft',
    'videos.studio.sidebar.finalizeHint':
      'Fir an Ärer Bibliothéik ze späicheren, benotzt de Knäppchen an der ieweschter Leescht.',
    'videos.studio.canvas.setupEmpty':
      'Liest de säitleche Panell, da wiesselt op Capture.',
    'videos.studio.stepSetup': 'Virbereedung',
    'videos.studio.stepCapture': 'Capture',
    'videos.studio.stepPost': 'Montage',
    'videos.studio.titleLabel': 'Video-Titel',
    'videos.studio.titlePlaceholder': 'Meng Opnam…',
    'videos.studio.saveSetup': 'Späicheren',
    'videos.studio.allowMedia': 'Ecran a Mikro erlaaben',
    'videos.studio.controlsHint':
      'Tipp: fir dës Leescht net mat opzehuelen, deelt e Tab oder e Fënster statt dem ganzen Ecran, oder benotzt en zweete Monitor. Espac = Paus / weider wann dës Säit de Fokus huet.',
    'videos.studio.controlsHintTab':
      'Dir deelt en aneren Tab: d’Opnam weist dës Studio-Säit net (d’Knäppercher si also net am Video). Hält d’Studio an enger anerer Browser-Fënster oder op engem zweete Ecran nieft Ärem Inhalt. Fir Paus, Stop oder Fäerdeg, wiesselt op d’Fënster wou d’Studio op ass (Alt+Tab oder Cmd+Tab). Espac funktionéiert nëmmen wann d’Studio-Säit de Fokus huet.',
    'videos.studio.controlsHintMonitor':
      'Dir deelt de ganzen Ecran: dës Leescht kann an der Opnam ze gesinn sinn. Wielt léiwer «Tab» oder «Fënster» beim Deelen, oder réckelt d’Steierung op en aneren Monitor.',
    'videos.studio.controlsHintWindow':
      'Dir deelt eng Fënster: de Rescht vum Desktop (aner Fënsteren) gëtt normalerweis net opgeholl. Benotzt d’Knäppercher hei ënnen fir Paus, Stop oder Fäerdeg.',
    'videos.studio.startRecord': 'Opnam starten',
    'videos.studio.pause': 'Paus',
    'videos.studio.resume': 'Weider',
    'videos.studio.stop': 'Stop',
    'videos.studio.terminer': 'Fäerdeg',
    'videos.studio.processingUpload': 'Eroplueden a Transkriptioun…',
    'videos.studio.timeline': 'Ofspillung',
    'videos.studio.transcript': 'Erkannten Text',
    'videos.studio.voiceMode': 'Audio-Spur',
    'videos.studio.voiceOriginal': 'Meng Stëmm (opgehollt Mikro)',
    'videos.studio.voiceAi': 'KI-Stëmm (Mistral TTS)',
    'videos.studio.voiceSelect': 'Stëmm',
    'videos.studio.polish': 'Text fir KI-Stëmm virbereeden',
    'videos.studio.generateTts': 'Voice-over generéieren',
    'videos.studio.applyVoiceover': 'KI-Stëmm op de Video uwenden',
    'videos.studio.preview': 'Virschau',
    'videos.studio.preview.noFile': 'Keng Videodatei fir d\'Virschau.',
    'videos.studio.bgImage': 'Hannergrondbild',
    'videos.studio.bgImageChoose': 'Wielen',
    'videos.studio.bgImageInvalidType': 'Ongëlteg Format. Benotzt e Bild (PNG, JPG, WebP).',
    'videos.studio.bgImageTooLarge': 'D\'Bild däerf net méi wéi 10 Mo grouss sinn.',
    'videos.studio.bgImageLoadError': 'D\'Hannergrondbild konnt net geluede ginn.',
    'videos.studio.bgImageVideoSize': 'Videogréisst',
    'videos.studio.dragToMove': 'Zéien fir ze réckelen',
    'videos.studio.finalize': 'An Bibliothéik späicheren',
    'videos.studio.back': 'Zréck bei d’Videoen',
    'videos.studio.errorMedia': 'Keen Zougang zum Ecran oder Mikro.',
    'videos.studio.errorInsecureContext':
      'Capture brauch HTTPS oder localhost. Öffnet d’App iwwer https:// oder http://127.0.0.1 / localhost.',
    'videos.studio.errorNotSupported':
      'Dëse Browser oder Apparat ënnerstëtzt hei keng Ecran-Capture. Benotzt aktuellen Chrome oder Edge um Desktop.',
    'videos.studio.errorMicDenied':
      'Mikro blockéiert: erlaabt de Mikro fir dës Säit (Schlass oder ⓘ an der Adressleescht), da nach eng Kéier probéieren.',
    'videos.studio.errorScreenDenied':
      'Ecran-Deelen ofgeleent: wielt am Browser-Dialog en Ecran oder Tab a confirméiert «Deelen».',
    'videos.studio.errorMicMissing':
      'Kee Mikro fonnt. Kontrolléiert Uschlëss a Systemastellungen.',
    'videos.studio.errorScreenMissing': 'Ecran-Deelen op dësem Apparat oder Browser net disponibel.',
    'videos.studio.errorMicBusy':
      'De Mikro gëtt vun enger anerer App benotzt. Maacht se zou a probéiert nach eng Kéier.',
    'videos.studio.errorScreenBusy':
      'Ecran konnt net erfaasst ginn (Ressource besat). Nach eng Kéier probéieren oder de Browser nei starten.',
    'videos.studio.errorScreenCancelled': 'Ecran-Deelen ofgebrach.',
    'videos.studio.errorMicCancelled': 'Mikro-Zougang ofgebrach.',
    'videos.studio.errorMicGeneric': 'Keng Zougang zum Mikro.',
    'videos.studio.errorScreenGeneric': 'Ecran konnt net erfaasst ginn.',
    'videos.studio.errorScreenEnded': 'Ecran-Deelen gestoppt.',
    'videos.studio.mediaHints':
      'Chrome oder Edge um Desktop. Mat http:// an enger LAN-IP (z. B. 192.168.x.x) blockéiert de Browser dacks: benotzt http://localhost:5173 oder valabel HTTPS.',
    'videos.studio.errorRecord': 'Opnam gëtt an dësem Browser net ënnerstëtzt.',
    'videos.studio.errorUpload': 'Eroplueden ass feelgeschloen.',
    'videos.studio.errorTranscribe': 'Transkriptioun net disponibel.',
    'videos.studio.errorPolish': 'KI-Textbearbechtung feelgeschloen.',
    'videos.studio.errorTts': 'Sproochsynthees feelgeschloen.',
    'videos.studio.errorMux': 'Audio zesummeféieren feelgeschloen.',
    'videos.studio.muxSuccess': 'KI-Stëmm op de Video ugewannt',
    'videos.studio.successFinal': 'Video an Ärer Bibliothéik gespäichert',
    'videos.studio.audioTrackPlanned': 'Geplangte Audiospur',
    'videos.studio.categoriesEmptyInstitution': 'Keng Kategorien sinn fir Är Institutioun konfiguréiert.',
    'videos.studio.categoriesHint': 'Eng oder méi Kategorien vun Ärer Institutioun (optional).',
    'videos.studio.crop': 'Zoueschneiden',
    'videos.studio.cropActive': 'Zoueschnëtt aktiv — zitt oder ännert de Beräich op dem Video',
    'videos.studio.cropApplied': 'Zoueschnëtt applizéiert',
    'videos.studio.cropDone': 'Fäerdeg',
    'videos.studio.cropEdit': 'Änneren',
    'videos.studio.cropRemove': 'Ewechhuelen',
    'videos.studio.cropReset': 'Zoueschnëtt zrécksetzen',
    'videos.studio.errorLoadDraft': 'Den Entworf konnt net geluede ginn.',
    'videos.studio.fieldCategories': 'Kategorien',
    'videos.studio.fieldDescription': 'Beschreiwung',
    'videos.studio.generatingTimeline': 'Zeitleist gëtt erstallt…',
    'videos.studio.postVoiceAiPipeline': 'KI-Stëmm: Text virbereeden, Audiospur generéieren an op de Video applizéieren.',
    'videos.studio.postVoiceHumanKeep': 'Dir hutt déi mënschlech Stëmm gewielt: de Video behält den Toun vum Mikro. Finaliséiert wann Dir prett sidd.',
    'videos.studio.processingPublish': 'Video gëtt veraarbecht…',
    'videos.studio.publish': 'Publizéieren',
    'videos.studio.saveDraft': 'Entworf',
    'videos.studio.successDraft': 'Video als Entworf gespäichert',
    'videos.studio.successPublish': 'Video an Ärer Bibliothéik publizéiert',
    'videos.studio.titleUntitled': 'Ouni Titel',
    'videos.studio.transcriptPlaceholder': 'Den erkannte Text vun Ärer Opnam erschéngt hei…',
    'videos.studio.transcriptRefining': 'Transkriptioun gëtt verbessert…',
    'videos.studio.trimDuration': 'Dauer',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'Keng KI-Stëmme sinn am Moment disponibel.',
    'videos.studio.voiceAiPlan': 'KI-Stëmm (Auswiel fir de Montage)',
    'videos.studio.voiceHumanPlan': 'Mënschlech Stëmm (opgeholl Mikro)',
    'videos.studio.warnTabOnly': 'Dir hutt een eenzelen Tab ausgewielt: nëmmen dësen Tab gëtt opgeholl, och wann Dir anerer opmaacht. Fir méi Tabs oder Fënsteren opzehuelen, start d\'Opnam nei a wielt „Ganzen Ecran“.',
    'videos.studio.templateMenuTitle': 'Modell-Menü',
    'videos.studio.templateEmpty': 'Keng gespäichert Modeller',
    'videos.studio.templatesHintCompact': 'Klickt op + fir e Modell ze späicheren, uwenden oder ze läschen.',
    'videos.studio.overlayAddMusic': 'Hannergrondmusek',
    'videos.studio.musicInvalidType': 'Ongültegt Format. Benotzt Audio (MP3, WAV, M4A, OGG).',
    'videos.studio.musicTooLarge': 'D\'Audiodatei däerf net méi grouss wéi 50 MB sinn.',
    'videos.studio.musicDefault': 'Musek',
    'videos.studio.musicAdded': 'Hannergrondmusek an d\'Timeline bäigefüügt.',
    'videos.studio.musicUploadError': 'Hannergrondmusek konnt net dobäigesat ginn.',
    'videos.studio.layerScreenBorder': 'Äusseren Rand',
    'videos.studio.layerScreenBorderSize': 'Gréisst',
    'videos.studio.layerScreenBorderColor': 'Faarf',
    'videos.studio.layerScreenBorderRadius': 'Eckeronnung',
    'videos.studio.segmentToolsTitle': 'Segment-Tools',
    'videos.studio.segmentZoom': 'Zoom',
    'videos.studio.segmentResetView': 'Vue zerécksetzen',
    'videos.studio.segmentTransition': 'Iwwergang',
    'videos.studio.transitionNone': 'Keng',
    'videos.studio.transitionFade': 'Fade',
    'videos.studio.transitionWipeLeft': 'Wësch →',
    'videos.studio.transitionSlideUp': 'Rutsch ↑',
    'videos.studio.textEditor.toolbar': 'Textformatéierung',
    'videos.studio.textEditor.bold': 'Fett',
    'videos.studio.textEditor.italic': 'Kursiv',
    'videos.studio.textEditor.underline': 'Ënnerstrach',
    'videos.studio.textEditor.textColor': 'Textfaarf (Auswiel)',
    'videos.studio.textEditor.size': 'Gréisst',
    'videos.studio.textEditor.font': 'Schrëft',
    'videos.studio.textEditor.fontFamily.system': 'System',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
  uk: {
    'videos.page.studioButton': 'Студія',
    'videos.studio.title': 'Відеостудія',
    'videos.studio.rail.prep': 'Підг.',
    'videos.studio.rail.capture': 'Захоплення',
    'videos.studio.rail.post': 'Монтаж',
    'videos.studio.rail.aria': 'Кроки відеостудії',
    'videos.studio.badge.setup': 'Підготовка',
    'videos.studio.badge.capture': 'Захоплення',
    'videos.studio.badge.recording': 'Запис',
    'videos.studio.badge.post': 'Монтаж',
    'videos.studio.cannotLeaveRecord': 'Зупиніть запис перед зміною кроку.',
    'videos.studio.railNeedRecording': 'Запишіть відео, щоб відкрити крок монтажу.',
    'videos.studio.sidebar.captureHelp':
      'Запис охоплює весь спільний потік (екран, вкладка або вікно). Обрізання корисної області буде на етапі монтажу.',
    'videos.studio.sidebar.sources': 'Джерела',
    'videos.studio.sidebar.recordingLive': 'Триває запис',
    'videos.studio.sidebar.finalizeHint':
      'Щоб зберегти в бібліотеці, скористайтеся кнопкою на верхній панелі.',
    'videos.studio.canvas.setupEmpty':
      'Прочитайте бічну панель, потім перейдіть до кроку «Захоплення».',
    'videos.studio.stepSetup': 'Підготовка',
    'videos.studio.stepCapture': 'Захоплення',
    'videos.studio.stepPost': 'Монтаж',
    'videos.studio.titleLabel': 'Назва відео',
    'videos.studio.titlePlaceholder': 'Мій запис…',
    'videos.studio.saveSetup': 'Зберегти',
    'videos.studio.allowMedia': 'Дозволити екран і мікрофон',
    'videos.studio.controlsHint':
      'Підказка: щоб не записувати цю панель, діліться вкладкою або вікном замість усього екрана або використайте другий монітор. Пробіл = пауза / відновлення, коли ця сторінка у фокусі.',
    'videos.studio.controlsHintTab':
      'Ви ділитеся іншою вкладкою: запис не містить цю сторінку студії (кнопки не потрапляють у відео). Тримайте студію в іншому вікні браузера або на другому моніторі поруч із контентом. Для Паузи, Стоп або Готово перейдіть до вікна зі студією (Alt+Tab або Cmd+Tab). Пробіл працює лише коли у фокусі сторінка студії.',
    'videos.studio.controlsHintMonitor':
      'Ви ділитеся всім екраном: ця панель може потрапити в запис. Краще оберіть «Вкладка» або «Вікно» в діалозі спільного доступу або перенесіть елементи керування на інший монітор.',
    'videos.studio.controlsHintWindow':
      'Ви ділитеся одним вікном: решта робочого столу (інші вікна) зазвичай не записується. Використовуйте кнопки нижче для Паузи, Стоп або Готово.',
    'videos.studio.startRecord': 'Записати',
    'videos.studio.pause': 'Пауза',
    'videos.studio.resume': 'Відновити',
    'videos.studio.stop': 'Стоп',
    'videos.studio.terminer': 'Готово',
    'videos.studio.processingUpload': 'Надсилання та транскрипція…',
    'videos.studio.timeline': 'Відтворення',
    'videos.studio.transcript': 'Розпізнаний текст',
    'videos.studio.voiceMode': 'Аудіодоріжка',
    'videos.studio.voiceOriginal': 'Мій голос (записаний мікрофон)',
    'videos.studio.voiceAi': 'Голос ШІ (Mistral TTS)',
    'videos.studio.voiceSelect': 'Голос',
    'videos.studio.polish': 'Підготувати текст для голосу ШІ',
    'videos.studio.generateTts': 'Згенерувати закадровий голос',
    'videos.studio.applyVoiceover': 'Застосувати голос ШІ до відео',
    'videos.studio.preview': 'Попередній перегляд',
    'videos.studio.preview.noFile': 'Немає відеофайлу для перегляду.',
    'videos.studio.bgImage': 'Фонове зображення',
    'videos.studio.bgImageChoose': 'Обрати',
    'videos.studio.bgImageInvalidType': 'Невірний формат. Використовуйте зображення (PNG, JPG, WebP).',
    'videos.studio.bgImageTooLarge': 'Зображення не повинно перевищувати 10 МБ.',
    'videos.studio.bgImageLoadError': 'Не вдалося завантажити фонове зображення.',
    'videos.studio.bgImageVideoSize': 'Розмір відео',
    'videos.studio.dragToMove': 'Перетягніть для переміщення',
    'videos.studio.finalize': 'Зберегти в бібліотеці',
    'videos.studio.back': 'Назад до відео',
    'videos.studio.errorMedia': 'Немає доступу до екрана або мікрофона.',
    'videos.studio.errorInsecureContext':
      'Захоплення екрана потребує HTTPS або localhost. Відкрийте застосунок через https:// або http://127.0.0.1 / localhost.',
    'videos.studio.errorNotSupported':
      'Цей браузер або пристрій не підтримує захоплення екрана тут. Використайте актуальний Chrome або Edge на комп’ютері.',
    'videos.studio.errorMicDenied':
      'Мікрофон заблоковано: дозвольте мікрофон для цього сайту (замок або ⓘ в адресному рядку), потім спробуйте знову.',
    'videos.studio.errorScreenDenied':
      'Спільний доступ до екрана відхилено: оберіть екран або вкладку в діалозі браузера й підтвердіть «Поділитися».',
    'videos.studio.errorMicMissing':
      'Мікрофон не знайдено. Перевірте підключення та системні налаштування.',
    'videos.studio.errorScreenMissing': 'Спільний доступ до екрана недоступний на цьому пристрої чи в браузері.',
    'videos.studio.errorMicBusy':
      'Мікрофон уже використовує інша програма. Закрийте її й спробуйте знову.',
    'videos.studio.errorScreenBusy':
      'Не вдалося захопити екран (ресурс зайнятий). Повторіть або перезапустіть браузер.',
    'videos.studio.errorScreenCancelled': 'Спільний доступ до екрана скасовано.',
    'videos.studio.errorMicCancelled': 'Доступ до мікрофона скасовано.',
    'videos.studio.errorMicGeneric': 'Немає доступу до мікрофона.',
    'videos.studio.errorScreenGeneric': 'Не вдалося захопити екран.',
    'videos.studio.errorScreenEnded': 'Спільний доступ до екрана зупинено.',
    'videos.studio.mediaHints':
      'Chrome або Edge на комп’ютері. Якщо URL — http:// з локальною IP (наприклад 192.168.x.x), браузер часто блокує: використайте http://localhost:5173 або дійсний HTTPS.',
    'videos.studio.errorRecord': 'Запис не підтримується в цьому браузері.',
    'videos.studio.errorUpload': 'Не вдалося надіслати.',
    'videos.studio.errorTranscribe': 'Транскрипція недоступна.',
    'videos.studio.errorPolish': 'Не вдалося обробити текст ШІ.',
    'videos.studio.errorTts': 'Не вдалося синтезувати мовлення.',
    'videos.studio.errorMux': 'Не вдалося об’єднати аудіо.',
    'videos.studio.muxSuccess': 'Голос ШІ застосовано до відео',
    'videos.studio.successFinal': 'Відео збережено в бібліотеці',
    'videos.studio.audioTrackPlanned': 'Запланована аудіодоріжка',
    'videos.studio.categoriesEmptyInstitution': 'Для вашої установи не налаштовано жодних категорій.',
    'videos.studio.categoriesHint': 'Одна або кілька категорій вашої установи (необов\'язково).',
    'videos.studio.crop': 'Обрізати',
    'videos.studio.cropActive': 'Обрізка активна — перетягніть або змініть розмір області на відео',
    'videos.studio.cropApplied': 'Обрізку застосовано',
    'videos.studio.cropDone': 'Готово',
    'videos.studio.cropEdit': 'Редагувати',
    'videos.studio.cropRemove': 'Видалити',
    'videos.studio.cropReset': 'Скинути обрізку',
    'videos.studio.errorLoadDraft': 'Не вдалося завантажити чернетку.',
    'videos.studio.fieldCategories': 'Категорії',
    'videos.studio.fieldDescription': 'Опис',
    'videos.studio.generatingTimeline': 'Генерація таймлайну…',
    'videos.studio.postVoiceAiPipeline': 'ШІ-голос: підготуйте текст, згенеруйте аудіодоріжку та застосуйте її до відео.',
    'videos.studio.postVoiceHumanKeep': 'Ви обрали людський голос: відео зберігає аудіо з мікрофона. Завершіть, коли будете готові.',
    'videos.studio.processingPublish': 'Обробка відео…',
    'videos.studio.publish': 'Опублікувати',
    'videos.studio.saveDraft': 'Чернетка',
    'videos.studio.successDraft': 'Відео збережено як чернетку',
    'videos.studio.successPublish': 'Відео опубліковано у вашій бібліотеці',
    'videos.studio.titleUntitled': 'Без назви',
    'videos.studio.transcriptPlaceholder': 'Розпізнаний текст вашого запису з\'явиться тут…',
    'videos.studio.transcriptRefining': 'Покращення транскрипції…',
    'videos.studio.trimDuration': 'тривалість',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'Наразі немає доступних ШІ-голосів.',
    'videos.studio.voiceAiPlan': 'ШІ-голос (вибір для монтажу)',
    'videos.studio.voiceHumanPlan': 'Людський голос (записаний мікрофон)',
    'videos.studio.warnTabOnly': 'Ви обрали одну вкладку: буде записано лише її, навіть якщо ви відкриєте інші. Щоб записати кілька вкладок або вікон, перезапустіть захоплення та оберіть «Весь екран».',
    'videos.studio.templateMenuTitle': 'Меню шаблонів',
    'videos.studio.templateEmpty': 'Немає збережених шаблонів',
    'videos.studio.templatesHintCompact': 'Натисніть +, щоб зберегти, застосувати або видалити шаблон.',
    'videos.studio.overlayAddMusic': 'Фонова музика',
    'videos.studio.musicInvalidType': 'Невірний формат. Використовуйте аудіо (MP3, WAV, M4A, OGG).',
    'videos.studio.musicTooLarge': 'Аудіофайл не повинен перевищувати 50 МБ.',
    'videos.studio.musicDefault': 'Музика',
    'videos.studio.musicAdded': 'Фонову музику додано на таймлайн.',
    'videos.studio.musicUploadError': 'Не вдалося додати фонову музику.',
    'videos.studio.layerScreenBorder': 'Зовнішня рамка',
    'videos.studio.layerScreenBorderSize': 'Розмір',
    'videos.studio.layerScreenBorderColor': 'Колір',
    'videos.studio.layerScreenBorderRadius': 'Заокруглення кутів',
    'videos.studio.segmentToolsTitle': 'Інструменти сегмента',
    'videos.studio.segmentZoom': 'Масштаб',
    'videos.studio.segmentResetView': 'Скинути вид',
    'videos.studio.segmentTransition': 'Переходи',
    'videos.studio.transitionNone': 'Немає',
    'videos.studio.transitionFade': 'Плавне згасання',
    'videos.studio.transitionWipeLeft': 'Протирання →',
    'videos.studio.transitionSlideUp': 'Зсув ↑',
    'videos.studio.textEditor.toolbar': 'Форматування тексту',
    'videos.studio.textEditor.bold': 'Жирний',
    'videos.studio.textEditor.italic': 'Курсив',
    'videos.studio.textEditor.underline': 'Підкреслений',
    'videos.studio.textEditor.textColor': 'Колір тексту (виділення)',
    'videos.studio.textEditor.size': 'Розмір',
    'videos.studio.textEditor.font': 'Шрифт',
    'videos.studio.textEditor.fontFamily.system': 'Системний',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
  ru: {
    'videos.page.studioButton': 'Студия',
    'videos.studio.title': 'Видеостудия',
    'videos.studio.rail.prep': 'Подг.',
    'videos.studio.rail.capture': 'Захват',
    'videos.studio.rail.post': 'Монтаж',
    'videos.studio.rail.aria': 'Этапы видеостудии',
    'videos.studio.badge.setup': 'Подготовка',
    'videos.studio.badge.capture': 'Захват',
    'videos.studio.badge.recording': 'Запись',
    'videos.studio.badge.post': 'Монтаж',
    'videos.studio.cannotLeaveRecord': 'Остановите запись перед сменой этапа.',
    'videos.studio.railNeedRecording': 'Запишите видео, чтобы открыть этап монтажа.',
    'videos.studio.sidebar.captureHelp':
      'Запись охватывает весь общий поток (экран, вкладка или окно). Обрезка полезной области будет на этапе монтажа.',
    'videos.studio.sidebar.sources': 'Источники',
    'videos.studio.sidebar.recordingLive': 'Идёт запись',
    'videos.studio.sidebar.finalizeHint':
      'Чтобы сохранить в библиотеке, используйте кнопку на верхней панели.',
    'videos.studio.canvas.setupEmpty':
      'Прочитайте боковую панель, затем перейдите к этапу «Захват».',
    'videos.studio.stepSetup': 'Подготовка',
    'videos.studio.stepCapture': 'Захват',
    'videos.studio.stepPost': 'Монтаж',
    'videos.studio.titleLabel': 'Название видео',
    'videos.studio.titlePlaceholder': 'Моя запись…',
    'videos.studio.saveSetup': 'Сохранить',
    'videos.studio.allowMedia': 'Разрешить экран и микрофон',
    'videos.studio.controlsHint':
      'Совет: чтобы не записывать эту панель, делитесь вкладкой или окном вместо всего экрана или используйте второй монитор. Пробел = пауза / возобновление, когда эта страница в фокусе.',
    'videos.studio.controlsHintTab':
      'Вы делитесь другой вкладкой: запись не включает эту страницу студии (кнопки не попадают в видео). Держите студию в другом окне браузера или на втором мониторе рядом с контентом. Для Паузы, Стоп или Готово переключитесь на окно со студией (Alt+Tab или Cmd+Tab). Пробел работает только когда в фокусе страница студии.',
    'videos.studio.controlsHintMonitor':
      'Вы делитесь всем экраном: эта панель может попасть в запись. Лучше выберите «Вкладка» или «Окно» в диалоге общего доступа или перенесите элементы управления на другой монитор.',
    'videos.studio.controlsHintWindow':
      'Вы делитесь одним окном: остальной рабочий стол (другие окна) обычно не записывается. Используйте кнопки ниже для Паузы, Стоп или Готово.',
    'videos.studio.startRecord': 'Запись',
    'videos.studio.pause': 'Пауза',
    'videos.studio.resume': 'Продолжить',
    'videos.studio.stop': 'Стоп',
    'videos.studio.terminer': 'Готово',
    'videos.studio.processingUpload': 'Загрузка и транскрипция…',
    'videos.studio.timeline': 'Воспроизведение',
    'videos.studio.transcript': 'Распознанный текст',
    'videos.studio.voiceMode': 'Аудиодорожка',
    'videos.studio.voiceOriginal': 'Мой голос (записанный микрофон)',
    'videos.studio.voiceAi': 'Голос ИИ (Mistral TTS)',
    'videos.studio.voiceSelect': 'Голос',
    'videos.studio.polish': 'Подготовить текст для голоса ИИ',
    'videos.studio.generateTts': 'Сгенерировать закадровый голос',
    'videos.studio.applyVoiceover': 'Применить голос ИИ к видео',
    'videos.studio.preview': 'Предпросмотр',
    'videos.studio.preview.noFile': 'Нет видеофайла для предпросмотра.',
    'videos.studio.bgImage': 'Фоновое изображение',
    'videos.studio.bgImageChoose': 'Выбрать',
    'videos.studio.bgImageInvalidType': 'Неверный формат. Используйте изображение (PNG, JPG, WebP).',
    'videos.studio.bgImageTooLarge': 'Изображение не должно превышать 10 МБ.',
    'videos.studio.bgImageLoadError': 'Не удалось загрузить фоновое изображение.',
    'videos.studio.bgImageVideoSize': 'Размер видео',
    'videos.studio.dragToMove': 'Перетащите для перемещения',
    'videos.studio.finalize': 'Сохранить в библиотеке',
    'videos.studio.back': 'Назад к видео',
    'videos.studio.errorMedia': 'Нет доступа к экрану или микрофону.',
    'videos.studio.errorInsecureContext':
      'Захват экрана требует HTTPS или localhost. Откройте приложение через https:// или http://127.0.0.1 / localhost.',
    'videos.studio.errorNotSupported':
      'Этот браузер или устройство не поддерживает захват экрана здесь. Используйте актуальный Chrome или Edge на компьютере.',
    'videos.studio.errorMicDenied':
      'Микрофон заблокирован: разрешите микрофон для этого сайта (замок или ⓘ в адресной строке), затем попробуйте снова.',
    'videos.studio.errorScreenDenied':
      'Общий доступ к экрану отклонён: выберите экран или вкладку в диалоге браузера и подтвердите «Поделиться».',
    'videos.studio.errorMicMissing':
      'Микрофон не найден. Проверьте подключение и системные настройки.',
    'videos.studio.errorScreenMissing': 'Общий доступ к экрану недоступен на этом устройстве или в браузере.',
    'videos.studio.errorMicBusy':
      'Микрофон уже использует другое приложение. Закройте его и попробуйте снова.',
    'videos.studio.errorScreenBusy':
      'Не удалось захватить экран (ресурс занят). Повторите или перезапустите браузер.',
    'videos.studio.errorScreenCancelled': 'Общий доступ к экрану отменён.',
    'videos.studio.errorMicCancelled': 'Доступ к микрофону отменён.',
    'videos.studio.errorMicGeneric': 'Нет доступа к микрофону.',
    'videos.studio.errorScreenGeneric': 'Не удалось захватить экран.',
    'videos.studio.errorScreenEnded': 'Общий доступ к экрану остановлен.',
    'videos.studio.mediaHints':
      'Chrome или Edge на компьютере. Если URL — http:// с локальным IP (например 192.168.x.x), браузер часто блокирует: используйте http://localhost:5173 или действительный HTTPS.',
    'videos.studio.errorRecord': 'Запись не поддерживается в этом браузере.',
    'videos.studio.errorUpload': 'Не удалось отправить.',
    'videos.studio.errorTranscribe': 'Транскрипция недоступна.',
    'videos.studio.errorPolish': 'Не удалось обработать текст ИИ.',
    'videos.studio.errorTts': 'Не удалось синтезировать речь.',
    'videos.studio.errorMux': 'Не удалось объединить аудио.',
    'videos.studio.muxSuccess': 'Голос ИИ применён к видео',
    'videos.studio.successFinal': 'Видео сохранено в библиотеке',
    'videos.studio.audioTrackPlanned': 'Запланированная аудиодорожка',
    'videos.studio.categoriesEmptyInstitution': 'Для вашего учреждения не настроены категории.',
    'videos.studio.categoriesHint': 'Одна или несколько категорий вашего учреждения (необязательно).',
    'videos.studio.crop': 'Обрезать',
    'videos.studio.cropActive': 'Обрезка активна — перетащите или измените размер области на видео',
    'videos.studio.cropApplied': 'Обрезка применена',
    'videos.studio.cropDone': 'Готово',
    'videos.studio.cropEdit': 'Редактировать',
    'videos.studio.cropRemove': 'Удалить',
    'videos.studio.cropReset': 'Сбросить обрезку',
    'videos.studio.errorLoadDraft': 'Не удалось загрузить черновик.',
    'videos.studio.fieldCategories': 'Категории',
    'videos.studio.fieldDescription': 'Описание',
    'videos.studio.generatingTimeline': 'Генерация таймлайна…',
    'videos.studio.postVoiceAiPipeline': 'ИИ-голос: подготовьте текст, сгенерируйте аудиодорожку и примените её к видео.',
    'videos.studio.postVoiceHumanKeep': 'Вы выбрали человеческий голос: видео сохраняет аудио с микрофона. Завершите, когда будете готовы.',
    'videos.studio.processingPublish': 'Обработка видео…',
    'videos.studio.publish': 'Опубликовать',
    'videos.studio.saveDraft': 'Черновик',
    'videos.studio.successDraft': 'Видео сохранено как черновик',
    'videos.studio.successPublish': 'Видео опубликовано в вашей библиотеке',
    'videos.studio.titleUntitled': 'Без названия',
    'videos.studio.transcriptPlaceholder': 'Распознанный текст вашей записи появится здесь…',
    'videos.studio.transcriptRefining': 'Улучшение транскрипции…',
    'videos.studio.trimDuration': 'длительность',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'В данный момент нет доступных ИИ-голосов.',
    'videos.studio.voiceAiPlan': 'ИИ-голос (выбор для монтажа)',
    'videos.studio.voiceHumanPlan': 'Человеческий голос (записанный микрофон)',
    'videos.studio.warnTabOnly': 'Вы выбрали одну вкладку: будет записана только она, даже если вы откроете другие. Чтобы записать несколько вкладок или окон, перезапустите захват и выберите «Весь экран».',
    'videos.studio.templateMenuTitle': 'Меню шаблонов',
    'videos.studio.templateEmpty': 'Нет сохранённых шаблонов',
    'videos.studio.templatesHintCompact': 'Нажмите +, чтобы сохранить, применить или удалить шаблон.',
    'videos.studio.overlayAddMusic': 'Фоновая музыка',
    'videos.studio.musicInvalidType': 'Неверный формат. Используйте аудио (MP3, WAV, M4A, OGG).',
    'videos.studio.musicTooLarge': 'Аудиофайл не должен превышать 50 МБ.',
    'videos.studio.musicDefault': 'Музыка',
    'videos.studio.musicAdded': 'Фоновая музыка добавлена на таймлайн.',
    'videos.studio.musicUploadError': 'Не удалось добавить фоновую музыку.',
    'videos.studio.layerScreenBorder': 'Внешняя рамка',
    'videos.studio.layerScreenBorderSize': 'Размер',
    'videos.studio.layerScreenBorderColor': 'Цвет',
    'videos.studio.layerScreenBorderRadius': 'Скругление углов',
    'videos.studio.segmentToolsTitle': 'Инструменты сегмента',
    'videos.studio.segmentZoom': 'Масштаб',
    'videos.studio.segmentResetView': 'Сбросить вид',
    'videos.studio.segmentTransition': 'Переход',
    'videos.studio.transitionNone': 'Нет',
    'videos.studio.transitionFade': 'Затухание',
    'videos.studio.transitionWipeLeft': 'Смахивание →',
    'videos.studio.transitionSlideUp': 'Сдвиг ↑',
    'videos.studio.textEditor.toolbar': 'Форматирование текста',
    'videos.studio.textEditor.bold': 'Жирный',
    'videos.studio.textEditor.italic': 'Курсив',
    'videos.studio.textEditor.underline': 'Подчёркнутый',
    'videos.studio.textEditor.textColor': 'Цвет текста (выделение)',
    'videos.studio.textEditor.size': 'Размер',
    'videos.studio.textEditor.font': 'Шрифт',
    'videos.studio.textEditor.fontFamily.system': 'Системный',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
  ar: {
    'videos.page.studioButton': 'الاستوديو',
    'videos.studio.title': 'استوديو الفيديو',
    'videos.studio.rail.prep': 'تحضير',
    'videos.studio.rail.capture': 'التقاط',
    'videos.studio.rail.post': 'مونتاج',
    'videos.studio.rail.aria': 'مراحل استوديو الفيديو',
    'videos.studio.badge.setup': 'تحضير',
    'videos.studio.badge.capture': 'التقاط',
    'videos.studio.badge.recording': 'تسجيل',
    'videos.studio.badge.post': 'مونتاج',
    'videos.studio.cannotLeaveRecord': 'أوقف التسجيل قبل تغيير المرحلة.',
    'videos.studio.railNeedRecording': 'سجّل فيديوًا للوصول إلى مرحلة المونتاج.',
    'videos.studio.sidebar.captureHelp':
      'التسجيل يلتقط البث المشارَك بالكامل (الشاشة أو علامة التبويب أو النافذة). سيُجرى قصّ المنطقة المفيدة لاحقًا في مرحلة المونتاج.',
    'videos.studio.sidebar.sources': 'المصادر',
    'videos.studio.sidebar.recordingLive': 'جارٍ التسجيل',
    'videos.studio.sidebar.finalizeHint':
      'للحفظ في مكتبتك، استخدم الزر في الشريط العلوي.',
    'videos.studio.canvas.setupEmpty':
      'اطّلع على اللوحة الجانبية، ثم انتقل إلى مرحلة الالتقاط.',
    'videos.studio.stepSetup': 'تحضير',
    'videos.studio.stepCapture': 'التقاط',
    'videos.studio.stepPost': 'مونتاج',
    'videos.studio.titleLabel': 'عنوان الفيديو',
    'videos.studio.titlePlaceholder': 'تسجيلي…',
    'videos.studio.saveSetup': 'حفظ',
    'videos.studio.allowMedia': 'السماح بالشاشة والميكروفون',
    'videos.studio.controlsHint':
      'نصيحة: لتجنب تسجيل هذا الشريط، شارِك علامة تبويب أو نافذة بدل الشاشة بأكملها، أو استخدم شاشة ثانية. المسافة = إيقاف مؤقت / استئناف عندما تكون هذه الصفحة في التركيز.',
    'videos.studio.controlsHintTab':
      'أنت تشارك علامة تبويب أخرى: التسجيل لا يظهر صفحة الاستوديو (فلا تظهر الأزرار في الفيديو). اترك الاستوديو في نافذة متصفح أخرى أو على شاشة ثانية بجانب المحتوى. للإيقاف المؤقت أو الإيقاف أو الإنهاء، انتقل إلى النافذة التي يفتح فيها الاستوديو (Alt+Tab أو Cmd+Tab). المسافة تعمل فقط عندما تكون صفحة الاستوديو في التركيز.',
    'videos.studio.controlsHintMonitor':
      'أنت تشارك الشاشة بأكملها: قد يظهر هذا الشريط في التسجيل. يفضّل اختيار «علامة تبويب» أو «نافذة» عند المشاركة، أو نقل عناصر التحكم إلى شاشة أخرى.',
    'videos.studio.controlsHintWindow':
      'أنت تشارك نافذة واحدة: لا يُسجَّل عادةً بقية سطح المكتب (النوافذ الأخرى). استخدم الأزرار أدناه للإيقاف المؤقت أو الإيقاف أو الإنهاء.',
    'videos.studio.startRecord': 'تسجيل',
    'videos.studio.pause': 'إيقاف مؤقت',
    'videos.studio.resume': 'استئناف',
    'videos.studio.stop': 'إيقاف',
    'videos.studio.terminer': 'إنهاء',
    'videos.studio.processingUpload': 'جارٍ الرفع والتحويل إلى نص…',
    'videos.studio.timeline': 'تشغيل',
    'videos.studio.transcript': 'النص المعترف به',
    'videos.studio.voiceMode': 'المسار الصوتي',
    'videos.studio.voiceOriginal': 'صوتي (ميكروفون مسجّل)',
    'videos.studio.voiceAi': 'صوت ذكاء اصطناعي (Mistral TTS)',
    'videos.studio.voiceSelect': 'الصوت',
    'videos.studio.polish': 'تهيئة النص للصوت بالذكاء الاصطناعي',
    'videos.studio.generateTts': 'إنشاء تعليق صوتي',
    'videos.studio.applyVoiceover': 'تطبيق الصوت الاصطناعي على الفيديو',
    'videos.studio.preview': 'معاينة',
    'videos.studio.preview.noFile': 'لا يوجد ملف فيديو للمعاينة.',
    'videos.studio.bgImage': 'صورة الخلفية',
    'videos.studio.bgImageChoose': 'اختيار',
    'videos.studio.bgImageInvalidType': 'تنسيق غير صالح. استخدم صورة (PNG، JPG، WebP).',
    'videos.studio.bgImageTooLarge': 'يجب ألا تتجاوز الصورة 10 ميغابايت.',
    'videos.studio.bgImageLoadError': 'تعذّر تحميل صورة الخلفية.',
    'videos.studio.bgImageVideoSize': 'حجم الفيديو',
    'videos.studio.dragToMove': 'اسحب للتحريك',
    'videos.studio.finalize': 'حفظ في المكتبة',
    'videos.studio.back': 'العودة إلى الفيديوهات',
    'videos.studio.errorMedia': 'تعذّر الوصول إلى الشاشة أو الميكروفون.',
    'videos.studio.errorInsecureContext':
      'يلزم التقاط الشاشة عبر HTTPS أو localhost. افتح التطبيق عبر https:// أو http://127.0.0.1 / localhost.',
    'videos.studio.errorNotSupported':
      'لا يدعم هذا المتصفح أو الجهاز التقاط الشاشة هنا. استخدم Chrome أو Edge محدّثًا على سطح المكتب.',
    'videos.studio.errorMicDenied':
      'الميكروفون محظور: اسمح بالميكروفون لهذا الموقع (أيقونة القفل أو ⓘ في شريط العنوان) ثم أعد المحاولة.',
    'videos.studio.errorScreenDenied':
      'رفض مشاركة الشاشة: اختر شاشة أو علامة تبويب في مربع المتصفح وأكّد «مشاركة».',
    'videos.studio.errorMicMissing':
      'لم يُعثر على ميكروفون. تحقق من التوصيل وإعدادات النظام.',
    'videos.studio.errorScreenMissing': 'مشاركة الشاشة غير متاحة على هذا الجهاز أو المتصفح.',
    'videos.studio.errorMicBusy':
      'الميكروفون مستخدم من تطبيق آخر. أغلقه ثم أعد المحاولة.',
    'videos.studio.errorScreenBusy':
      'تعذّر التقاط الشاشة (المورد مشغول). أعد المحاولة أو أعد تشغيل المتصفح.',
    'videos.studio.errorScreenCancelled': 'أُلغيت مشاركة الشاشة.',
    'videos.studio.errorMicCancelled': 'أُلغي الوصول إلى الميكروفون.',
    'videos.studio.errorMicGeneric': 'تعذّر الوصول إلى الميكروفون.',
    'videos.studio.errorScreenGeneric': 'تعذّر التقاط الشاشة.',
    'videos.studio.errorScreenEnded': 'توقفت مشاركة الشاشة.',
    'videos.studio.mediaHints':
      'Chrome أو Edge على سطح المكتب. إذا كان العنوان http:// مع عنوان IP محلي (مثل 192.168.x.x)، يحظر المتصفح غالبًا: استخدم http://localhost:5173 أو HTTPS صالحًا.',
    'videos.studio.errorRecord': 'التسجيل غير مدعوم في هذا المتصفح.',
    'videos.studio.errorUpload': 'فشل الرفع.',
    'videos.studio.errorTranscribe': 'النسخ غير متاح.',
    'videos.studio.errorPolish': 'فشلت معالجة النص بالذكاء الاصطناعي.',
    'videos.studio.errorTts': 'فشل تحويل النص إلى كلام.',
    'videos.studio.errorMux': 'فشل دمج الصوت.',
    'videos.studio.muxSuccess': 'طُبّق الصوت الاصطناعي على الفيديو',
    'videos.studio.successFinal': 'حُفظ الفيديو في مكتبتك',
    'videos.studio.audioTrackPlanned': 'المسار الصوتي المخطط',
    'videos.studio.categoriesEmptyInstitution': 'لم يتم تكوين فئات لمؤسستكم.',
    'videos.studio.categoriesHint': 'فئة واحدة أو أكثر من مؤسستكم (اختياري).',
    'videos.studio.crop': 'اقتصاص',
    'videos.studio.cropActive': 'الاقتصاص نشط — اسحب أو غيّر حجم المنطقة على الفيديو',
    'videos.studio.cropApplied': 'تم تطبيق الاقتصاص',
    'videos.studio.cropDone': 'تم',
    'videos.studio.cropEdit': 'تعديل',
    'videos.studio.cropRemove': 'إزالة',
    'videos.studio.cropReset': 'إعادة تعيين الاقتصاص',
    'videos.studio.errorLoadDraft': 'تعذّر تحميل المسودة.',
    'videos.studio.fieldCategories': 'الفئات',
    'videos.studio.fieldDescription': 'الوصف',
    'videos.studio.generatingTimeline': 'جارٍ إنشاء الخط الزمني…',
    'videos.studio.postVoiceAiPipeline': 'صوت ذكاء اصطناعي: حضّر النص، أنشئ المسار الصوتي ثم طبّقه على الفيديو.',
    'videos.studio.postVoiceHumanKeep': 'اخترتم الصوت البشري: يحتفظ الفيديو بصوت الميكروفون. أنهوا عندما تكونون جاهزين.',
    'videos.studio.processingPublish': 'جارٍ معالجة الفيديو…',
    'videos.studio.publish': 'نشر',
    'videos.studio.saveDraft': 'مسودة',
    'videos.studio.successDraft': 'تم حفظ الفيديو كمسودة',
    'videos.studio.successPublish': 'تم نشر الفيديو في مكتبتكم',
    'videos.studio.titleUntitled': 'بدون عنوان',
    'videos.studio.transcriptPlaceholder': 'سيظهر النص المُتعرَّف عليه من تسجيلكم هنا…',
    'videos.studio.transcriptRefining': 'جارٍ تحسين النسخ…',
    'videos.studio.trimDuration': 'المدة',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'لا تتوفر أصوات ذكاء اصطناعي حاليًا.',
    'videos.studio.voiceAiPlan': 'صوت ذكاء اصطناعي (خيار للمونتاج)',
    'videos.studio.voiceHumanPlan': 'صوت بشري (ميكروفون مسجّل)',
    'videos.studio.warnTabOnly': 'لقد حددتم علامة تبويب واحدة: سيتم تسجيل تلك العلامة فقط حتى لو فتحتم علامات أخرى. لالتقاط عدة علامات أو نوافذ، أعيدوا بدء الالتقاط واختاروا «الشاشة بالكامل».',
    'videos.studio.templateMenuTitle': 'قائمة القوالب',
    'videos.studio.templateEmpty': 'لا توجد قوالب محفوظة',
    'videos.studio.templatesHintCompact': 'انقروا على + لحفظ قالب أو تطبيقه أو حذفه.',
    'videos.studio.overlayAddMusic': 'موسيقى خلفية',
    'videos.studio.musicInvalidType': 'تنسيق غير صالح. استخدموا ملفًا صوتيًا (MP3 أو WAV أو M4A أو OGG).',
    'videos.studio.musicTooLarge': 'يجب ألا يتجاوز ملف الصوت 50 ميغابايت.',
    'videos.studio.musicDefault': 'موسيقى',
    'videos.studio.musicAdded': 'تمت إضافة موسيقى الخلفية إلى الخط الزمني.',
    'videos.studio.musicUploadError': 'تعذرت إضافة موسيقى الخلفية.',
    'videos.studio.layerScreenBorder': 'حد خارجي',
    'videos.studio.layerScreenBorderSize': 'الحجم',
    'videos.studio.layerScreenBorderColor': 'اللون',
    'videos.studio.layerScreenBorderRadius': 'استدارة الزوايا',
    'videos.studio.segmentToolsTitle': 'أدوات المقطع',
    'videos.studio.segmentZoom': 'تكبير',
    'videos.studio.segmentResetView': 'إعادة ضبط العرض',
    'videos.studio.segmentTransition': 'الانتقال',
    'videos.studio.transitionNone': 'بدون',
    'videos.studio.transitionFade': 'تلاشي',
    'videos.studio.transitionWipeLeft': 'مسح →',
    'videos.studio.transitionSlideUp': 'انزلاق ↑',
    'videos.studio.textEditor.toolbar': 'تنسيق النص',
    'videos.studio.textEditor.bold': 'عريض',
    'videos.studio.textEditor.italic': 'مائل',
    'videos.studio.textEditor.underline': 'تحته خط',
    'videos.studio.textEditor.textColor': 'لون النص (التحديد)',
    'videos.studio.textEditor.size': 'الحجم',
    'videos.studio.textEditor.font': 'الخط',
    'videos.studio.textEditor.fontFamily.system': 'النظام',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
  tr: {
    'videos.page.studioButton': 'Stüdyo',
    'videos.studio.title': 'Video Stüdyosu',
    'videos.studio.rail.prep': 'Hazırlık',
    'videos.studio.rail.capture': 'Çekim',
    'videos.studio.rail.post': 'Kurgu',
    'videos.studio.rail.aria': 'Video stüdyosu adımları',
    'videos.studio.badge.setup': 'Hazırlık',
    'videos.studio.badge.capture': 'Çekim',
    'videos.studio.badge.recording': 'Kayıt',
    'videos.studio.badge.post': 'Kurgu',
    'videos.studio.cannotLeaveRecord': 'Adım değiştirmeden önce kaydı durdurun.',
    'videos.studio.railNeedRecording': 'Kurgu adımını açmak için bir video kaydedin.',
    'videos.studio.sidebar.captureHelp':
      'Kayıt, paylaşılan akışın tamamını yakalar (ekran, sekme veya pencere). Kullanılabilir alana kırpma işlemi kurgu aşamasında yapılacaktır.',
    'videos.studio.sidebar.sources': 'Kaynaklar',
    'videos.studio.sidebar.recordingLive': 'Kayıt devam ediyor',
    'videos.studio.sidebar.finalizeHint':
      'Kütüphanenize kaydetmek için üst çubuktaki düğmeyi kullanın.',
    'videos.studio.canvas.setupEmpty':
      'Yan paneli okuyun, ardından Çekim adımına geçin.',
    'videos.studio.stepSetup': 'Hazırlık',
    'videos.studio.stepCapture': 'Çekim',
    'videos.studio.stepPost': 'Kurgu',
    'videos.studio.titleLabel': 'Video başlığı',
    'videos.studio.titlePlaceholder': 'Kaydım…',
    'videos.studio.saveSetup': 'Kaydet',
    'videos.studio.allowMedia': 'Ekran ve mikrofona izin ver',
    'videos.studio.controlsHint':
      'İpucu: Bu çubuğu kaydetmemek için ekranın tamamı yerine bir sekme veya pencere paylaşın ya da ikinci bir monitör kullanın. Boşluk çubuğu = bu sayfa odaktayken duraklat / devam et.',
    'videos.studio.controlsHintTab':
      'Başka bir sekme paylaşıyorsunuz: kayıt bu stüdyo sayfasını içermez (düğmeler videoda görünmez). Stüdyoyu başka bir tarayıcı penceresinde veya içeriğinizin yanında ikinci bir ekranda açık tutun. Duraklat, Durdur veya Bitir için stüdyonun açık olduğu pencereye geçin (Alt+Tab veya Cmd+Tab). Boşluk çubuğu yalnızca stüdyo sayfası odaktayken çalışır.',
    'videos.studio.controlsHintMonitor':
      'Ekranın tamamını paylaşıyorsunuz: bu çubuk kayıtta görünebilir. Paylaşımda «Sekme» veya «Pencere»yi tercih edin ya da kontrolleri başka bir monitöre taşıyın.',
    'videos.studio.controlsHintWindow':
      'Bir pencere paylaşıyorsunuz: masaüstünün geri kalanı (diğer pencereler) genellikle kaydedilmez. Duraklat, Durdur veya Bitir için aşağıdaki düğmeleri kullanın.',
    'videos.studio.startRecord': 'Kaydet',
    'videos.studio.pause': 'Duraklat',
    'videos.studio.resume': 'Devam et',
    'videos.studio.stop': 'Durdur',
    'videos.studio.terminer': 'Bitir',
    'videos.studio.processingUpload': 'Yükleme ve transkript…',
    'videos.studio.timeline': 'Oynatma',
    'videos.studio.transcript': 'Tanınan metin',
    'videos.studio.voiceMode': 'Ses kanalı',
    'videos.studio.voiceOriginal': 'Benim sesim (kaydedilen mikrofon)',
    'videos.studio.voiceAi': 'Yapay Zeka Sesi (Mistral TTS)',
    'videos.studio.voiceSelect': 'Ses',
    'videos.studio.polish': 'Yapay zeka sesi için metni hazırla',
    'videos.studio.generateTts': 'Seslendirme oluştur',
    'videos.studio.applyVoiceover': 'Yapay zeka sesini videoya uygula',
    'videos.studio.preview': 'Önizleme',
    'videos.studio.preview.noFile': 'Önizlenecek video dosyası yok.',
    'videos.studio.bgImage': 'Arka plan görseli',
    'videos.studio.bgImageChoose': 'Seç',
    'videos.studio.bgImageInvalidType': 'Geçersiz format. Bir görsel kullanın (PNG, JPG, WebP).',
    'videos.studio.bgImageTooLarge': 'Görsel en fazla 10 MB olmalıdır.',
    'videos.studio.bgImageLoadError': 'Arka plan görseli yüklenemedi.',
    'videos.studio.bgImageUploading': 'Yükleniyor…',
    'videos.studio.bgImageUploadError': 'Görsel yüklenemedi.',
    'videos.studio.bgImageLibraryTitle': 'Video stüdyosu arka planı',
    'videos.studio.postLayoutTitle': 'Çerçeve ve Arka Plan',
    'videos.studio.postLayoutHint':
      'Önizleme yayınlanan dosyaya karşılık gelir: taslağı kaydedin, ardından yayınlayın.',
    'videos.studio.bgImageVideoSize': 'Video boyutu',
    'videos.studio.dragToMove': 'Taşımak için sürükle',
    'videos.studio.finalize': 'Kütüphaneye kaydet',
    'videos.studio.back': 'Videolara geri dön',
    'videos.studio.errorMedia': 'Ekran veya mikrofona erişilemiyor.',
    'videos.studio.errorInsecureContext':
      'Ekran yakalama HTTPS veya localhost gerektirir. Uygulamayı https:// veya http://127.0.0.1 / localhost üzerinden açın.',
    'videos.studio.errorNotSupported':
      'Bu tarayıcı veya cihaz burada ekran yakalamayı desteklemiyor. Masaüstünde güncel Chrome veya Edge kullanın.',
    'videos.studio.errorMicDenied':
      'Mikrofon engellendi: bu site için mikrofona izin verin (adres çubuğundaki kilit veya ⓘ simgesi), ardından tekrar deneyin.',
    'videos.studio.errorScreenDenied':
      'Ekran paylaşımı reddedildi: tarayıcı iletişim kutusunda bir ekran veya sekme seçin ve «Paylaş»ı onaylayın.',
    'videos.studio.errorMicMissing':
      'Mikrofon bulunamadı. Donanımı ve sistem ayarlarını kontrol edin.',
    'videos.studio.errorScreenMissing': 'Bu cihazda veya tarayıcıda ekran paylaşımı kullanılamıyor.',
    'videos.studio.errorMicBusy':
      'Mikrofon başka bir uygulama tarafından kullanılıyor. Uygulamayı kapatın ve tekrar deneyin.',
    'videos.studio.errorScreenBusy':
      'Ekran yakalanamadı (kaynak meşgul). Tekrar deneyin veya tarayıcıyı yeniden başlatın.',
    'videos.studio.errorScreenCancelled': 'Ekran paylaşımı iptal edildi.',
    'videos.studio.errorMicCancelled': 'Mikrofon erişimi iptal edildi.',
    'videos.studio.errorMicGeneric': 'Mikrofona erişilemiyor.',
    'videos.studio.errorScreenGeneric': 'Ekran yakalanamadı.',
    'videos.studio.errorScreenEnded': 'Ekran paylaşımı sonlandırıldı.',
    'videos.studio.mediaHints':
      'Masaüstünde Chrome veya Edge. URL http:// ile yerel ağ IP\'si (ör. 192.168.x.x) ise tarayıcı genellikle engeller: http://localhost:5173 veya geçerli HTTPS kullanın.',
    'videos.studio.errorRecord': 'Kayıt bu tarayıcıda desteklenmiyor.',
    'videos.studio.errorUpload': 'Yükleme başarısız.',
    'videos.studio.errorTranscribe': 'Transkript kullanılamıyor.',
    'videos.studio.errorPolish': 'Yapay zeka metin işleme başarısız.',
    'videos.studio.errorTts': 'Ses sentezi başarısız.',
    'videos.studio.errorMux': 'Ses birleştirme başarısız.',
    'videos.studio.muxSuccess': 'Yapay zeka sesi videoya uygulandı',
    'videos.studio.successFinal': 'Video kütüphanenize kaydedildi',
    'videos.studio.audioTrackPlanned': 'Planlanan ses kanalı',
    'videos.studio.categoriesEmptyInstitution': 'Kurumunuz için yapılandırılmış kategori yok.',
    'videos.studio.categoriesHint': 'Kurumunuza ait bir veya daha fazla kategori (isteğe bağlı).',
    'videos.studio.crop': 'Kırp',
    'videos.studio.cropActive': 'Kırpma etkin — videodaki alanı sürükleyin veya yeniden boyutlandırın',
    'videos.studio.cropApplied': 'Kırpma uygulandı',
    'videos.studio.cropDone': 'Tamam',
    'videos.studio.cropEdit': 'Düzenle',
    'videos.studio.cropRemove': 'Kaldır',
    'videos.studio.cropReset': 'Kırpmayı sıfırla',
    'videos.studio.errorLoadDraft': 'Taslak yüklenemedi.',
    'videos.studio.fieldCategories': 'Kategoriler',
    'videos.studio.fieldDescription': 'Açıklama',
    'videos.studio.generatingTimeline': 'Zaman çizelgesi oluşturuluyor…',
    'videos.studio.postVoiceAiPipeline': 'Yapay zeka sesi: metni hazırlayın, ses kanalını oluşturun ve videoya uygulayın.',
    'videos.studio.postVoiceHumanKeep': 'İnsan sesi seçildi: video mikrofon sesini korur. Hazır olduğunuzda sonlandırın.',
    'videos.studio.processingPublish': 'Video işleniyor…',
    'videos.studio.publish': 'Yayınla',
    'videos.studio.saveDraft': 'Taslak',
    'videos.studio.successDraft': 'Video taslak olarak kaydedildi',
    'videos.studio.successPublish': 'Video kütüphanenizde yayınlandı',
    'videos.studio.titleUntitled': 'Başlıksız',
    'videos.studio.transcriptPlaceholder': 'Kaydınızdan tanınan metin burada görünecek…',
    'videos.studio.transcriptRefining': 'Transkript iyileştiriliyor…',
    'videos.studio.trimDuration': 'süre',
    'videos.studio.trimIn': 'IN',
    'videos.studio.trimOut': 'OUT',
    'videos.studio.voiceAiNoVoices': 'Şu anda kullanılabilir yapay zeka sesi yok.',
    'videos.studio.voiceAiPlan': 'Yapay zeka sesi (kurgu için seçim)',
    'videos.studio.voiceHumanPlan': 'İnsan sesi (kaydedilen mikrofon)',
    'videos.studio.warnTabOnly': 'Tek bir sekme seçtiniz: başkalarını açsanız bile yalnızca bu sekme kaydedilecektir. Birden fazla sekme veya pencere kaydetmek için yakalamayı yeniden başlatın ve «Tüm Ekran»ı seçin.',
    'videos.studio.templateMenuTitle': 'Şablon menüsü',
    'videos.studio.templateEmpty': 'Kayıtlı şablon yok',
    'videos.studio.templatesHintCompact': 'Şablon kaydetmek, uygulamak veya silmek için +\'ya tıklayın.',
    'videos.studio.overlayAddMusic': 'Arka plan müziği',
    'videos.studio.musicInvalidType': 'Geçersiz format. Ses dosyası kullanın (MP3, WAV, M4A, OGG).',
    'videos.studio.musicTooLarge': 'Ses dosyası en fazla 50 MB olmalıdır.',
    'videos.studio.musicDefault': 'Müzik',
    'videos.studio.musicAdded': 'Arka plan müziği zaman çizelgesine eklendi.',
    'videos.studio.musicUploadError': 'Arka plan müziği eklenemedi.',
    'videos.studio.layerScreenBorder': 'Dış kenarlık',
    'videos.studio.layerScreenBorderSize': 'Boyut',
    'videos.studio.layerScreenBorderColor': 'Renk',
    'videos.studio.layerScreenBorderRadius': 'Köşe yuvarlaklığı',
    'videos.studio.segmentToolsTitle': 'Bölüm araçları',
    'videos.studio.segmentZoom': 'Yakınlaştırma',
    'videos.studio.segmentResetView': 'Görünümü sıfırla',
    'videos.studio.segmentTransition': 'Geçiş',
    'videos.studio.transitionNone': 'Yok',
    'videos.studio.transitionFade': 'Solma',
    'videos.studio.transitionWipeLeft': 'Silme →',
    'videos.studio.transitionSlideUp': 'Kaydırma ↑',
    'videos.studio.textEditor.toolbar': 'Metin biçimlendirme',
    'videos.studio.textEditor.bold': 'Kalın',
    'videos.studio.textEditor.italic': 'İtalik',
    'videos.studio.textEditor.underline': 'Altı çizili',
    'videos.studio.textEditor.textColor': 'Metin rengi (seçim)',
    'videos.studio.textEditor.size': 'Boyut',
    'videos.studio.textEditor.font': 'Yazı tipi',
    'videos.studio.textEditor.fontFamily.system': 'Sistem',
    'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
    'videos.studio.textEditor.fontFamily.serif': 'Serif',
    'videos.studio.textEditor.fontFamily.mono': 'Mono',
    'videos.studio.textEditor.fontFamily.script': 'Script',
  },
};
